import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, Loader2, Calendar, ChevronDown, Lightbulb } from 'lucide-react';

import { api } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { loadStockList } from '../../data/stockList';
import { getBrokerSummaryBlobPath, parseBrokerSummaryCSV, fetchBrokerSummaryCsv } from '../../utils/brokerSummaryBlob';
import { fetchSectorMapping } from '../../utils/stockMetadataBlob';

interface BrokerSummaryData {
  broker: string;
  buyerVol: number;   // BuyerVol (for BLot in BUY table)
  buyerValue: number; // BuyerValue (for BVal in BUY table)
  bavg: number;       // BuyerAvg (for BAvg in BUY table)
  sellerVol: number;  // SellerVol (for SLot in SELL table)
  sellerValue: number; // SellerValue (for SVal in SELL table)
  savg: number;       // SellerAvg (for SAvg in SELL table)
  // Net Buy fields (always >= 0, if negative becomes 0 and goes to NetSell)
  netBuyVol: number;  // NetBuyVol (for NBLot in NET table)
  netBuyValue: number; // NetBuyValue (for NBVal in NET table)
  netBuyerAvg: number; // NetBuyerAvg (for NBAvg in NET table)
  // Net Sell fields (always >= 0)
  netSellVol: number;  // NetSellVol (for NSLot in NET table)
  netSellValue: number; // NetSellValue (for NSVal in NET table)
  netSellerAvg: number; // NetSellerAvg (for NSAvg in NET table)
  // Legacy fields for backward compatibility
  nblot: number;      // Legacy: same as netBuyVol
  nbval: number;      // Legacy: same as netBuyValue
  sl: number;         // Legacy: same as sellerVol
  nslot: number;      // Legacy: negative sellerVol (not used anymore, use netSellVol instead)
  nsval: number;      // Legacy: negative sellerValue (not used anymore, use netSellValue instead)
}

// Note: TICKERS constant removed - now using dynamic stock loading from API

// Foreign brokers (red background)
const FOREIGN_BROKERS = [
  "AG", "AH", "AI", "AK", "BK", "BQ", "CG", "CS", "DP", "DR", "DU", "FS", "GW", "HD", "KK",
  "KZ", "LH", "LG", "LS", "MS", "RB", "RX", "TX", "YP", "YU", "ZP"
];

// Government brokers (green background)
const GOVERNMENT_BROKERS = ['CC', 'NI', 'OD', 'DX'];

// Note: local generator removed in favor of backend API
// Default dates (last 3 days) are now set from API data in component useEffect

// Minimum date that can be selected: 10/02/2020 (earliest Stockbit migration data)
const MIN_DATE = '2020-02-10';

// Page ID for menu preferences
const PAGE_ID = 'broker-activity-summary';

// Interface for user preferences
interface UserPreferences {
  selectedTickers: string[];
  fdFilter: 'All' | 'Foreign' | 'Domestic';
  marketFilter: 'RG' | 'TN' | 'NG' | '';
  startDate?: string;
  endDate?: string;
}

// Import menu preferences service
import { menuPreferencesService } from '../../services/menuPreferences';

// Utility functions for saving/loading preferences (now using cookies)
const loadPreferences = (pageId: string): Partial<UserPreferences> | null => {
  try {
    const cached = menuPreferencesService.getCachedPreferences(pageId);
    if (cached) {
      return cached as Partial<UserPreferences>;
    }
    const prefs = menuPreferencesService.loadPreferences(pageId);
    return prefs ? prefs as Partial<UserPreferences> : null;
  } catch (error) {
    console.warn('Failed to load cached preferences:', error);
  }
  return null;
};

const savePreferences = (pageId: string, prefs: Partial<UserPreferences>) => {
  menuPreferencesService.savePreferences(pageId, prefs);
};

const formatNumber = (value: number): string => {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1000000000) {
    return `${sign}${(absValue / 1000000000).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}B`;
  } else if (absValue >= 1000000) {
    return `${sign}${(absValue / 1000000).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`;
  } else if (absValue >= 1000) {
    return `${sign}${(absValue / 1000).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}K`;
  }
  return value.toLocaleString('en-US');
};

// Format Lot: jika >= 1,000,000 gunakan format dengan M (25.6M), jika < 1,000,000 gunakan format koma ribuan biasa (12,000)
const formatLot = (value: number): string => {
  const rounded = Math.round(value);
  const absRounded = Math.abs(rounded);
  if (absRounded >= 1000000) {
    // Format avec M pour nilai >= 1,000,000 ou <= -1,000,000
    const millions = rounded / 1000000;
    return `${millions.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`;
  }
  // Format koma ribuan biasa
  return rounded.toLocaleString('en-US');
};

const formatAverage = (value: number): string => {
  if (value === undefined || value === null || isNaN(value)) {
    return '-';
  }
  if (value === 0) {
    return '0.0';
  }
  // User request: Avg tidak perlu tanda minus
  const absValue = Math.abs(value);
  return absValue.toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
};

const formatDisplayDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

interface BrokerSummaryPageProps {
  selectedStock?: string;
  disableTickerSelection?: boolean; // When true, only use propSelectedStock and hide ticker selection UI
  isDashboard?: boolean;
  isActive?: boolean;
}

export function BrokerSummaryPage({ selectedStock: propSelectedStock, disableTickerSelection = false, isDashboard = false, isActive = true }: BrokerSummaryPageProps) {
  const { showToast } = useToast();

  // Load preferences from cookies on mount
  const effectivePageId = isDashboard ? `dashboard-${PAGE_ID}` : PAGE_ID;
  const [savedPrefs, setSavedPrefs] = useState(() => loadPreferences(effectivePageId));

  // Load preferences from cookies on mount
  useEffect(() => {
    if (savedPrefs) {
      if (savedPrefs['selectedTickers'] && savedPrefs['selectedTickers'].length > 0) {
        setSelectedTickers(savedPrefs['selectedTickers']);
      }
      if (savedPrefs['fdFilter']) {
        setFdFilter(savedPrefs['fdFilter']);
      }
      if (savedPrefs['marketFilter']) {
        setMarketFilter(savedPrefs['marketFilter']);
      }
    }
  }, [savedPrefs]);

  // Default dates will be set from API (maxAvailableDate useEffect)
  // Using empty initial state - will be populated from API
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const isSameStockInit = savedPrefs?.selectedStock === propSelectedStock;
  const [startDate, setStartDate] = useState<string>(() => {
    // Try to load from preferences first (unless in dashboard mode and stock changed)
    if ((!isDashboard || isSameStockInit) && savedPrefs?.startDate) {
      return savedPrefs.startDate;
    }
    // Fallback to empty (will be set from API)
    return '';
  });
  const [endDate, setEndDate] = useState<string>(() => {
    // Try to load from preferences first (unless in dashboard mode and stock changed)
    if ((!isDashboard || isSameStockInit) && savedPrefs?.endDate) {
      return savedPrefs.endDate;
    }
    // Fallback to empty (will be set from API)
    return '';
  });
  const [selectedTickers, setSelectedTickers] = useState<string[]>(() => {
    // Try to load from preferences first
    if (savedPrefs?.selectedTickers && savedPrefs.selectedTickers.length > 0) {
      return savedPrefs.selectedTickers;
    }
    // Fallback to prop or default
    return propSelectedStock ? [propSelectedStock] : ['BBCA'];
  });
  const [tickerInput, setTickerInput] = useState<string>('');
  const [fdFilter, setFdFilter] = useState<'All' | 'Foreign' | 'Domestic'>(() => {
    // Try to load from preferences first
    if (savedPrefs?.fdFilter) {
      return savedPrefs.fdFilter;
    }
    // Fallback to default
    return 'All';
  }); // Temporary filter (can be changed before Show button clicked)
  const [displayedFdFilter, setDisplayedFdFilter] = useState<'All' | 'Foreign' | 'Domestic'>('All'); // Actual filter used for data display (updated when Show button clicked)
  const [marketFilter, setMarketFilter] = useState<'RG' | 'TN' | 'NG' | ''>(() => {
    // Try to load from preferences first
    if (savedPrefs?.marketFilter) {
      return savedPrefs.marketFilter;
    }
    // Fallback to default
    return 'RG';
  }); // Default to RG
  const [displayedTickers, setDisplayedTickers] = useState<string[]>(propSelectedStock ? [propSelectedStock] : ['BBCA']); // Tickers displayed in header (updated when Show button clicked)
  const [displayedMarket, setDisplayedMarket] = useState<'RG' | 'TN' | 'NG' | ''>('RG'); // Market/Board displayed in header (updated when Show button clicked)
  // Track if menu wraps to 2 rows (Removed unused isMenuTwoRows component state)

  // Stock selection state
  const [availableStocks, setAvailableStocks] = useState<string[]>([]);
  const [sectorMapping, setSectorMapping] = useState<{ [sector: string]: string[] }>({}); // Sector -> list of stocks
  const [showStockSuggestions, setShowStockSuggestions] = useState(false);
  const [showAIPopup, setShowAIPopup] = useState(false);
  const [closePriceForAI, setClosePriceForAI] = useState<number | null>(null);

  const [stockSearchTimeout, setStockSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);
  const valueTableRef = useRef<HTMLTableElement>(null);
  const netTableRef = useRef<HTMLTableElement>(null);
  const valueTableContainerRef = useRef<HTMLDivElement>(null);
  const netTableContainerRef = useRef<HTMLDivElement>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);


  // API-driven broker summary data by date
  const [summaryByDate, setSummaryByDate] = useState<Map<string, BrokerSummaryData[]>>(new Map()); // Filtered data (based on displayedFdFilter)
  const [foreignSummaryByDate, setForeignSummaryByDate] = useState<Map<string, BrokerSummaryData[]>>(new Map()); // Global Foreign Data (Board All, Investor F) for FNVal
  const [rawSummaryByDate, setRawSummaryByDate] = useState<Map<string, BrokerSummaryData[]>>(new Map()); // Raw data without investor filter (for client-side filtering)
  const [lastFetchParams, setLastFetchParams] = useState<{ tickers: string[]; dates: string[]; market: string; fdFilter?: string } | null>(null); // Track last fetch parameters
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isDataReady, setIsDataReady] = useState<boolean>(false); // Control when to show tables
  const [shouldFetchData, setShouldFetchData] = useState<boolean>(false); // Control when to fetch data (only when Show button clicked)
  const [maxAvailableDate, setMaxAvailableDate] = useState<string>(''); // Maximum date available from API (format: YYYY-MM-DD)
  const shouldFetchDataRef = useRef<boolean>(false); // Ref to track shouldFetchData for async functions (always up-to-date)
  const abortControllerRef = useRef<AbortController | null>(null); // Ref to abort ongoing fetch



  // Enhanced cache system with persistence and size limits
  // Key format: `${ticker}-${date}-${market}` or `sector-${sectorName}-${date}-${market}`
  const dataCacheRef = useRef<Map<string, { data: BrokerSummaryData[]; timestamp: number }>>(new Map());

  // Cache configuration
  const CACHE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes (increased from 5 minutes)
  const CACHE_STORAGE_KEY = 'broker_summary_data_cache';
  const MAX_CACHE_SIZE = 500; // Maximum number of cache entries (to prevent memory overflow)
  const MAX_CACHE_AGE_DAYS = 1; // Cache can persist for 1 day in localStorage

  // Load cache from localStorage on mount
  useEffect(() => {
    try {
      const cached = localStorage.getItem(CACHE_STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        const now = Date.now();
        const maxAge = MAX_CACHE_AGE_DAYS * 24 * 60 * 60 * 1000;

        // Filter out expired entries
        const validEntries: Array<[string, { data: BrokerSummaryData[]; timestamp: number }]> = [];
        for (const [key, value] of Object.entries(parsed)) {
          const entry = value as { data: BrokerSummaryData[]; timestamp: number };
          if (now - entry.timestamp < maxAge) {
            validEntries.push([key, entry]);
          }
        }

        // Restore valid cache entries
        if (validEntries.length > 0) {
          dataCacheRef.current = new Map(validEntries);
          console.log(`[BrokerSummary] Loaded ${validEntries.length} cache entries from localStorage`);
        }
      }
    } catch (e) {
      console.warn('[BrokerSummary] Failed to load cache from localStorage:', e);
    }
  }, []);

  // Helper function to save cache to localStorage (debounced)
  const saveCacheToStorageRef = useRef<NodeJS.Timeout | null>(null);
  const saveCacheToStorage = () => {
    if (saveCacheToStorageRef.current) {
      clearTimeout(saveCacheToStorageRef.current);
    }

    saveCacheToStorageRef.current = setTimeout(() => {
      try {
        const cacheObj: { [key: string]: { data: BrokerSummaryData[]; timestamp: number } } = {};
        dataCacheRef.current.forEach((value, key) => {
          cacheObj[key] = value;
        });

        // Limit size before saving (keep most recent entries)
        const entries = Object.entries(cacheObj);
        if (entries.length > MAX_CACHE_SIZE) {
          // Sort by timestamp (newest first) and keep only MAX_CACHE_SIZE
          entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
          const limitedEntries = entries.slice(0, MAX_CACHE_SIZE);
          const limitedObj: { [key: string]: { data: BrokerSummaryData[]; timestamp: number } } = {};
          limitedEntries.forEach(([key, value]) => {
            limitedObj[key] = value;
          });
          localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(limitedObj));
          console.log(`[BrokerSummary] Cache size limited to ${MAX_CACHE_SIZE} entries`);
        } else {
          localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(cacheObj));
        }
      } catch (e) {
        // Handle quota exceeded error
        if (e instanceof Error && e.name === 'QuotaExceededError') {
          console.warn('[BrokerSummary] localStorage quota exceeded, clearing old cache entries');
          // Clear oldest 50% of cache
          const entries = Array.from(dataCacheRef.current.entries());
          entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
          const toRemove = entries.slice(0, Math.floor(entries.length / 2));
          toRemove.forEach(([key]) => {
            dataCacheRef.current.delete(key);
          });
          // Retry saving
          setTimeout(() => saveCacheToStorage(), 100);
        } else {
          console.warn('[BrokerSummary] Failed to save cache to localStorage:', e);
        }
      }
    }, 1000); // Debounce: save 1 second after last update
  };

  // Cleanup expired cache entries periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      let cleaned = 0;

      for (const [key, value] of dataCacheRef.current.entries()) {
        if (now - value.timestamp > CACHE_EXPIRY_MS) {
          dataCacheRef.current.delete(key);
          cleaned++;
        }
      }

      // Also check size limit
      if (dataCacheRef.current.size > MAX_CACHE_SIZE) {
        // Remove oldest entries
        const entries = Array.from(dataCacheRef.current.entries());
        entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
        const toRemove = entries.slice(0, dataCacheRef.current.size - MAX_CACHE_SIZE);
        toRemove.forEach(([key]) => {
          dataCacheRef.current.delete(key);
          cleaned++;
        });
      }

      if (cleaned > 0) {
        console.log(`[BrokerSummary] Cleaned up ${cleaned} expired cache entries`);
        saveCacheToStorage();
      }
    }, 5 * 60 * 1000); // Cleanup every 5 minutes

    return () => clearInterval(cleanupInterval);
  }, []);

  // Check if more than 7 days selected - if so, only show Total column
  // CRITICAL: Calculate from summaryByDate (data that exists), not from selectedDates (which changes on date picker)
  // This ensures no re-render when user changes dates - only when data changes (after Show button clicked)
  const showOnlyTotal = summaryByDate.size > 7;

  // Set max selectable date and default dates on mount - computed locally (no blob check)
  // IMPORTANT: This effect runs ONLY ONCE on mount - NO auto-fetch data
  useEffect(() => {
    // Allow selecting up to today in the calendar, regardless of whether broker
    // summary data has been generated yet for the current trading day (don't
    // gate the max date on a blob's latest-available-date check).
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    setMaxAvailableDate(todayStr);

    const isSameStock = savedPrefs?.selectedStock === propSelectedStock;
    // Set default dates if no preferences exist, OR if in dashboard mode but the stock has changed
    if ((isDashboard && !isSameStock) || !savedPrefs?.startDate || !savedPrefs?.endDate) {
      // Default to the last 3 trading days (Mon-Fri)
      const lastThreeDates: string[] = [];
      const cursor = new Date(today);
      const currentHour = today.getHours();
      if (currentHour < 20) {
        cursor.setDate(cursor.getDate() - 1); // Skip today if before 16:00
      }
      while (lastThreeDates.length < 3) {
        const dayOfWeek = cursor.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          const year = cursor.getFullYear();
          const month = String(cursor.getMonth() + 1).padStart(2, '0');
          const day = String(cursor.getDate()).padStart(2, '0');
          lastThreeDates.unshift(`${year}-${month}-${day}`);
        }
        cursor.setDate(cursor.getDate() - 1);
      }

      setSelectedDates(lastThreeDates);
      setStartDate(lastThreeDates[0]!);
      setEndDate(lastThreeDates[lastThreeDates.length - 1]!);

      if (isDashboard && propSelectedStock && isActive) {
        // Let the dashboardAutoFetchRef timeout handle the initial fetch
        // to ensure all filters and display states are properly synced
      }
    } else {
      // Use saved dates and generate selectedDates from them
      const savedStart = savedPrefs.startDate;
      const savedEnd = savedPrefs.endDate;

      setStartDate(savedStart);
      setEndDate(savedEnd);

      // Generate trading days between saved dates
      const startParts = savedStart.split('-').map(Number);
      const endParts = savedEnd.split('-').map(Number);

      if (startParts.length === 3 && endParts.length === 3) {
        const start = new Date(startParts[0] as number, (startParts[1] as number) - 1, startParts[2] as number);
        const end = new Date(endParts[0] as number, (endParts[1] as number) - 1, endParts[2] as number);

        if (start <= end) {
          const dateArray: string[] = [];
          const currentDate = new Date(start);

          while (currentDate <= end) {
            const dayOfWeek = currentDate.getDay();
            // Skip weekends
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
              const year = currentDate.getFullYear();
              const month = String(currentDate.getMonth() + 1).padStart(2, '0');
              const day = String(currentDate.getDate()).padStart(2, '0');
              const dateString = `${year}-${month}-${day}`;
              dateArray.push(dateString);
            }
            currentDate.setDate(currentDate.getDate() + 1);
          }

          const datesToUse = dateArray.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
          setSelectedDates(datesToUse);
        }
      }
    }
  }, [isDashboard, savedPrefs, isActive, propSelectedStock]); // Include isActive

  // Save preferences to localStorage with debounce to reduce write operations
  useEffect(() => {
    const timeout = setTimeout(() => {
      const preferences: Partial<UserPreferences> = {
        selectedTickers,
        fdFilter,
        marketFilter,
        selectedStock: propSelectedStock || (selectedTickers.length > 0 ? selectedTickers[0] : undefined)
      };
      // Only include dates if they have values
      if (startDate) {
        preferences.startDate = startDate;
      }
      if (endDate) {
        preferences.endDate = endDate;
      }

      savePreferences(effectivePageId, preferences);
      setSavedPrefs(prev => ({ ...prev, ...preferences }));
    }, 500); // Debounce 500ms to reduce localStorage writes

    return () => clearTimeout(timeout);
  }, [selectedTickers, fdFilter, marketFilter, startDate, endDate, propSelectedStock, effectivePageId]);

  // Auto-load data from saved preferences on mount (after initial data is loaded)
  useEffect(() => {
    // Only auto-load if we have saved preferences with dates AND we are not in dashboard mode
    if (isDashboard || !savedPrefs?.startDate || !savedPrefs?.endDate) {
      return; // No saved preferences or dashboard mode, don't auto-load
    }

    const savedStartDate = savedPrefs.startDate;
    const savedEndDate = savedPrefs.endDate;

    // Wait a bit to ensure initial data (stocks, dates) are loaded
    const timer = setTimeout(() => {
      // Generate date array from saved startDate and endDate (only trading days)
      const startParts = savedStartDate.split('-').map(Number);
      const endParts = savedEndDate.split('-').map(Number);

      if (startParts.length === 3 && endParts.length === 3) {
        const startYear = startParts[0];
        const startMonth = startParts[1];
        const startDay = startParts[2];
        const endYear = endParts[0];
        const endMonth = endParts[1];
        const endDay = endParts[2];

        if (startYear !== undefined && startMonth !== undefined && startDay !== undefined &&
          endYear !== undefined && endMonth !== undefined && endDay !== undefined) {
          const start = new Date(startYear, startMonth - 1, startDay);
          const end = new Date(endYear, endMonth - 1, endDay);

          // Check if range is valid
          if (start <= end) {
            // Generate date array (only trading days)
            const dateArray: string[] = [];
            const currentDate = new Date(start);

            while (currentDate <= end) {
              const dayOfWeek = currentDate.getDay();
              // Skip weekends (Saturday = 6, Sunday = 0)
              if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                // Format as YYYY-MM-DD in local timezone
                const year = currentDate.getFullYear();
                const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                const day = String(currentDate.getDate()).padStart(2, '0');
                const dateString = `${year}-${month}-${day}`;
                dateArray.push(dateString);
              }
              // Move to next day
              currentDate.setDate(currentDate.getDate() + 1);
            }

            // Sort by date (oldest first) for display
            const datesToUse = dateArray.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

            // Update selectedDates
            setSelectedDates(datesToUse);

            // Trigger auto-load by setting shouldFetchData to true
            // Use ref first (synchronous), then state (async)
            shouldFetchDataRef.current = true;
            setShouldFetchData(true);
          }
        }
      }
    }, 500); // Small delay to ensure initial data is loaded

    return () => clearTimeout(timer);
  }, []); // Only run once on mount

  // Update selectedTickers when prop changes
  // If disableTickerSelection is true, always force selectedTickers to match propSelectedStock
  useEffect(() => {
    if (disableTickerSelection && propSelectedStock) {
      // Force to use only propSelectedStock
      setSelectedTickers([propSelectedStock]);
      setTickerInput('');
    } else if (propSelectedStock && !selectedTickers.includes(propSelectedStock)) {
      setSelectedTickers([propSelectedStock]);
      setTickerInput('');
    }
  }, [propSelectedStock, disableTickerSelection]);

  // Auto-trigger data fetch when isDashboard is true (on mount and when propSelectedStock changes)
  // This eliminates the need for MutationObserver auto-click hacks in wrapper components
  const dashboardAutoFetchRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isDashboard || !propSelectedStock || !isActive) return;
    // Only auto-fetch if stock changed or first time
    if (dashboardAutoFetchRef.current === propSelectedStock) return;
    
    // Wait for ticker to be set and dates to be initialized
    const timer = setTimeout(() => {
      if (selectedDates.length > 0 && selectedTickers.length > 0) {
        dashboardAutoFetchRef.current = propSelectedStock;
        setDisplayedFdFilter(fdFilter);
        setDisplayedTickers([...selectedTickers]);
        setDisplayedMarket(marketFilter);
        setLastFetchParams({
          tickers: [...selectedTickers],
          dates: [...selectedDates],
          market: marketFilter || '',
          fdFilter: fdFilter
        });
        setSummaryByDate(new Map());
        setRawSummaryByDate(new Map());
        setIsDataReady(false);
        dataCacheRef.current.clear();
        shouldFetchDataRef.current = true;
        setShouldFetchData(true);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isDashboard, propSelectedStock, selectedDates, selectedTickers, isActive]);

  // Toast "Range Tanggal Lebih dari 7 Hari" is now shown only after clicking Show button (in fetchAll)

  // Load available stocks ONCE on mount - OPTIMIZED with localStorage cache for instant display
  // This is faster than getBrokerSummaryStocks() which requires a date
  useEffect(() => {
    const loadStocks = async () => {
      if (availableStocks.length > 0) return; // Already loaded

      // Cache keys and TTL
      const STOCK_LIST_CACHE_KEY = 'broker_summary_stock_list';
      const SECTOR_MAPPING_CACHE_KEY = 'broker_summary_sector_mapping';
      const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

      // Helper function to process and combine stocks + sectors
      const processStocksAndSectors = (
        stocks: string[],
        sectors: string[]
      ): string[] => {
        // Remove IDX from ticker list (IDX is now a sector, not a ticker)
        const stocksWithoutIdx = stocks.filter(stock => stock !== 'IDX');

        // Add sectors to the list (with prefix to distinguish from stocks)
        // IDX should already be in sectors from backend
        const sectorsWithPrefix = sectors.map(sector => `[SECTOR] ${sector}`);

        // Combine stocks and sectors, then sort alphabetically
        // Ensure IDX is always first
        return [...stocksWithoutIdx, ...sectorsWithPrefix].sort((a: string, b: string) => {
          // IDX always comes first
          if (a === '[SECTOR] IDX') return -1;
          if (b === '[SECTOR] IDX') return 1;
          
          const aIsSector = a.startsWith('[SECTOR] ');
          const bIsSector = b.startsWith('[SECTOR] ');
          
          // Put warrants (codes > 4 letters) at the bottom
          if (!aIsSector && !bIsSector) {
            const aIsWarrant = a.length > 4;
            const bIsWarrant = b.length > 4;
            if (aIsWarrant && !bIsWarrant) return 1;
            if (!aIsWarrant && bIsWarrant) return -1;
          }

          return a.localeCompare(b);
        });
      };

      // Try to load from cache first for instant display
      let cachedStocks: string[] | null = null;
      let cachedSectorMapping: { sectors: string[]; sectorMapping: { [sector: string]: string[] }; timestamp: number } | null = null;

      try {
        // Load cached stock list
        const cachedStockData = localStorage.getItem(STOCK_LIST_CACHE_KEY);
        if (cachedStockData) {
          const parsed = JSON.parse(cachedStockData);
          if (parsed.timestamp && (Date.now() - parsed.timestamp) < CACHE_TTL && parsed.stocks && Array.isArray(parsed.stocks)) {
            cachedStocks = parsed.stocks;
          }
        }

        // Load cached sector mapping
        const cachedSectorData = localStorage.getItem(SECTOR_MAPPING_CACHE_KEY);
        if (cachedSectorData) {
          const parsed = JSON.parse(cachedSectorData);
          if (parsed.timestamp && (Date.now() - parsed.timestamp) < CACHE_TTL) {
            cachedSectorMapping = {
              sectors: parsed.sectors || [],
              sectorMapping: parsed.sectorMapping || {},
              timestamp: parsed.timestamp
            };
          }
        }

        // If we have both cached data, use them immediately for instant display
        if (cachedStocks && cachedSectorMapping) {
          // Ensure IDX is in sectors list (IDX is special - not in sectorMapping)
          if (!cachedSectorMapping.sectors.includes('IDX')) {
            cachedSectorMapping.sectors.push('IDX');
          }

          const allItems = processStocksAndSectors(
            cachedStocks,
            cachedSectorMapping.sectors
          );
          setAvailableStocks(allItems);
          setSectorMapping(cachedSectorMapping.sectorMapping);
          console.log(`[BrokerSummary] Loaded ${cachedStocks.length} stocks and ${cachedSectorMapping.sectors.length} sectors from cache (instant display)`);
        }
      } catch (e) {
        // Ignore cache errors, will fetch from API
        console.warn('[BrokerSummary] Cache read error, will fetch from API:', e);
      }

      // Fetch from API in background (even if cache exists, to update cache)
      try {
        console.log('[BrokerSummary] Fetching stock list and sector mapping from API...');

        // Load both stock list and sector mapping in parallel
        const [_, sectorResult] = await Promise.all([
          loadStockList(), // Load stock list from stockList.ts
          cachedSectorMapping ? Promise.resolve({ success: true, data: cachedSectorMapping }) : fetchSectorMapping()
        ]);

        let stocks: string[] = [];
        let sectors: string[] = [];
        let mapping: { [sector: string]: string[] } = {};

        // Get stocks from stockList.ts (already loaded by loadStockList)
        const { getStockList } = await import('../../data/stockList');
        stocks = getStockList();

        // Cache stock list
        try {
          localStorage.setItem(STOCK_LIST_CACHE_KEY, JSON.stringify({
            stocks,
            timestamp: Date.now()
          }));
        } catch (e) {
          // Ignore cache write errors
        }

        if (sectorResult.success && sectorResult.data) {
          sectors = sectorResult.data.sectors || [];
          mapping = sectorResult.data.sectorMapping || {};

          // Ensure IDX is in sectors list (IDX is a special sector that appears in menu)
          // Note: IDX is NOT in sectorMapping because it's not a regular sector with multiple stocks
          // IDX is a special ticker that exists as IDX.csv in broker_summary folders
          if (!sectors.includes('IDX')) {
            sectors.push('IDX');
            console.log('[BrokerSummary] Added IDX to sectors list (IDX is special - not in sectorMapping)');
          }

          setSectorMapping(mapping);

          // Cache sector mapping
          try {
            localStorage.setItem(SECTOR_MAPPING_CACHE_KEY, JSON.stringify({
              sectors,
              sectorMapping: mapping,
              timestamp: Date.now()
            }));
          } catch (e) {
            // Ignore cache write errors
          }
        }

        // Process and update available stocks (will update if cache was stale or missing)
        const allItems = processStocksAndSectors(stocks, sectors);
        setAvailableStocks(allItems);
        console.log(`[BrokerSummary] Loaded ${stocks.filter(s => s !== 'IDX').length} stocks and ${sectors.length} sectors from API`);
      } catch (err) {
        console.error('[BrokerSummary] Error loading stock list:', err);
        // Even if API fails, ensure IDX is available as sector (if cache also failed)
        if (availableStocks.length === 0) {
          setAvailableStocks(['[SECTOR] IDX']);
        }
      }
    };

    loadStocks();
  }, []); // Only run once on mount

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (stockSearchTimeout) {
        clearTimeout(stockSearchTimeout);
      }
    };
  }, [stockSearchTimeout]);

  // REMOVED: useLayoutEffect that resets shouldFetchData
  // Reset is now handled directly in onChange handlers (startDate and endDate)
  // This ensures NO effect runs when user changes dates - completely silent

  // REMOVED: Initial auto-fetch tracking - no longer needed since we don't auto-fetch on mount

  // REMOVED: Reminder toast effect
  // User wants NO side effects when changing dates - completely silent
  // Reminder toast removed per user request: "jangan lakukan apa-apa di manapun, biarkan aja begitu"

  // REMOVED: Auto-fetch on initial load - user MUST click Show button to fetch data
  // This ensures no data is displayed automatically without user clicking Show

  // Load broker summary data from backend for each selected date and aggregate multiple tickers
  // Only fetch when shouldFetchData is true (triggered by Show button ONLY)
  // IMPORTANT: This effect only runs when shouldFetchData changes, NOT when selectedTickers/selectedDates change
  // CRITICAL: This effect should NEVER run on mount - only when user clicks Show button
  useEffect(() => {
    // CRITICAL: Check state FIRST - if false, don't fetch
    if (!shouldFetchData) {
      // Reset ref to match state
      shouldFetchDataRef.current = false;
      return; // Early return if shouldFetchData is false
    }

    // CRITICAL: Also check ref for consistency
    if (!shouldFetchDataRef.current) {
      return; // Early return if ref is false - this is the secondary guard
    }

    // CRITICAL: Sync ref with state before proceeding
    shouldFetchDataRef.current = true;

    // CRITICAL: Cancel any ongoing fetch before starting new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Final check: make sure ref is still true after cleanup
    if (!shouldFetchDataRef.current) {
      return;
    }

    // Create new AbortController for this fetch
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const fetchAll = async () => {
      // CRITICAL: Check ref instead of state - ref is always up-to-date even in async context
      // This is especially important if user changes dates while fetch is queued
      if (!shouldFetchDataRef.current) {
        setIsLoading(false);
        setIsDataReady(false);
        return;
      }

      if (selectedTickers.length === 0 || selectedDates.length === 0) {
        setIsLoading(false);
        setIsDataReady(false);
        shouldFetchDataRef.current = false;
        setShouldFetchData(false); // Reset fetch trigger
        return;
      }

      // Process selected tickers - sectors are now fetched directly from backend (no expansion)
      // Backend handles sector aggregation, frontend just fetches the sector CSV
      const tickersToFetch: string[] = [];

      selectedTickers.forEach(ticker => {
        if (ticker.startsWith('[SECTOR] ')) {
          const sectorName = ticker.replace('[SECTOR] ', '');
          // Fetch sector CSV directly from backend (backend already aggregated)
          tickersToFetch.push(sectorName);
          console.log(`[BrokerSummary] Sector ${sectorName} selected - will fetch sector CSV directly from backend`);
        } else {
          // Regular stock - fetch directly
          tickersToFetch.push(ticker);
        }
      });

      // Remove duplicates
      const uniqueTickers = Array.from(new Set(tickersToFetch));

      console.log(`[BrokerSummary] Processing ${selectedTickers.length} selected tickers (${uniqueTickers.length} unique):`, uniqueTickers);

      if (uniqueTickers.length === 0) {
        console.warn('[BrokerSummary] No tickers to fetch');
        setIsLoading(false);
        setIsDataReady(false);
        shouldFetchDataRef.current = false;
        setShouldFetchData(false);
        return;
      }

      // Note: maxAvailableDate validation is done in date picker, not here
      // If we reach here, dates should already be valid

      // CRITICAL: Check ref again after validation - user might have changed dates
      if (!shouldFetchDataRef.current) {
        setIsLoading(false);
        setIsDataReady(false);
        return;
      }

      // Validate that all selected dates are within maxAvailableDate
      if (maxAvailableDate && maxAvailableDate.trim() !== '') {
        const invalidDates = selectedDates.filter(date => date > maxAvailableDate);
        if (invalidDates.length > 0) {
          showToast({
            type: 'warning',
            title: 'Tanggal Tidak Valid',
            message: `Tanggal paling baru yang tersedia adalah ${new Date(maxAvailableDate).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}. Beberapa tanggal yang dipilih melebihi batas maksimum.`,
          });
          setIsLoading(false);
          setIsDataReady(false);
          shouldFetchDataRef.current = false;
          setShouldFetchData(false); // Reset fetch trigger
          return;
        }
      }

      // CRITICAL: Final check before starting fetch - user might have changed dates during validation
      if (!shouldFetchDataRef.current) {
        setIsLoading(false);
        setIsDataReady(false);
        return;
      }

      // Start loading (data and tables already cleared by the separate useEffect above)
      // But add safety check here too to ensure tables are hidden
      setIsDataReady(false);
      setIsLoading(true);
      setError(null);

      try {
        const market = marketFilter || '';
        const cache = dataCacheRef.current;

        // Map displayedFdFilter to investorType for API (user's main view)
        const mainInvestorType = displayedFdFilter === 'All' ? '' :
          displayedFdFilter === 'Domestic' ? 'D' : 'F';

        // Get a SAS token to fetch broker_summary CSVs directly from Azure Blob.
        // The backend only gates/authorizes access here; data is read client-side.
        const tokenRes = await api.getBrokerSummarySasToken();
        if (!tokenRes.success || !tokenRes.data) {
          throw new Error(tokenRes.error || 'Failed to get blob access token');
        }
        const { containerUrl, sasToken } = tokenRes.data;

        // Helper function to fetch data for a single ticker-date combination (with cache)
        // Modified to accept market and investorType as arguments
        const fetchSingleData = async (
          ticker: string,
          date: string,
          marketDesc: string,
          investorDesc: string,
          skipCache: boolean = false
        ): Promise<{ ticker: string; date: string; data: BrokerSummaryData[] }> => {
          // CRITICAL: Check if fetch was aborted
          if (abortController.signal.aborted || !shouldFetchDataRef.current) {
            throw new Error('Fetch aborted');
          }

          const cacheKey = `${ticker}-${date}-${marketDesc}-${investorDesc}`;
          const now = Date.now();
          const cached = cache.get(cacheKey);

          // Check cache first (skip cache on retry)
          // Cache is valid if it exists and hasn't expired
          if (!skipCache && cached) {
            const age = now - cached.timestamp;
            if (age <= CACHE_EXPIRY_MS) {
              console.log(`[BrokerSummary] Cache HIT: ${ticker} on ${date} (age: ${Math.round(age / 1000)}s) [${marketDesc}/${investorDesc}]`);
              return { ticker, date, data: cached.data };
            } else {
              // Cache expired, remove it
              cache.delete(cacheKey);
              console.log(`[BrokerSummary] Cache EXPIRED: ${ticker} on ${date} (age: ${Math.round(age / 1000)}s)`);
            }
          } else if (!skipCache) {
            console.log(`[BrokerSummary] Cache MISS: ${ticker} on ${date} [${marketDesc}/${investorDesc}]`);
          }

          // CRITICAL: Check again before API call
          if (abortController.signal.aborted || !shouldFetchDataRef.current) {
            throw new Error('Fetch aborted');
          }

          // Check if this is a sector or IDX
          const isSector = selectedTickers.some(t => t.startsWith('[SECTOR] ') && t.replace('[SECTOR] ', '') === ticker);

          // Fetch from API
          // Use new FD API if filter is F or D, otherwise use regular API
          // Update: Allow market filter for IDX and Sectors to support market-specific summaries (e.g. broker_summary_idx_rg)

          let marketToUse = marketDesc;
          // User request: kalau yang sector dia mappingnya ambil dari data azure dengan pathnya ini jika user memilih boardnya antara tn ng rg dan f/d nya all
          // Path: broker_summary_sector_[board]/broker_summary_sector_[board]_[tanggal]/[sector].csv
          // Logic: passing 'sector_RG' implies backend will look in 'broker_summary_sector_rg' folder
          // FIX: IDX should NOT be treated as a regular sector folder, it has its own folder broker_summary_idx_[board]
          if (isSector && ticker !== 'IDX' && (marketDesc === 'RG' || marketDesc === 'TN' || marketDesc === 'NG') && investorDesc === '') {
            marketToUse = `sector_${marketDesc}`;
          }

          // Fetch and parse the CSV directly from Azure Blob Storage
          const marketForPath = (investorDesc === 'F' || investorDesc === 'D') ? marketDesc : marketToUse;
          const blobPaths = getBrokerSummaryBlobPath(ticker, date, marketForPath, investorDesc as '' | 'F' | 'D');

          const csvText = await fetchBrokerSummaryCsv(blobPaths, containerUrl, sasToken, abortController.signal);

          if (!csvText) {
            console.error(`[BrokerSummary] No broker summary data found for ${ticker} on ${date}`);
            return { ticker, date, data: [] };
          }

          const brokerData = parseBrokerSummaryCSV(csvText);
          if (brokerData.length === 0) {
            console.warn(`[BrokerSummary] No broker data in response for ${ticker} on ${date}`);
            return { ticker, date, data: [] };
          }

          const rows: BrokerSummaryData[] = brokerData.map((r: any) => {
            // Backend already calculates NetSellVol, NetSellValue, NetBuyerAvg, NetSellerAvg
            // Backend also handles the logic: if NetBuy is negative, it becomes NetSell and NetBuy = 0
            // IMPORTANT: 
            // - For IDX: Calculate NetBuy/NetSell directly from BuyerVol - SellerVol in frontend (more precise)
            // - For sectors: NetBuy/NetSell need to be swapped because CSV has swap
            // - For regular stocks: use as is from CSV

            // Standardized logic (1:1 Mapping) - Backend has been fixed to be consistent
            // Buyer = BRK_COD1, Seller = BRK_COD2
            // No more date-based or type-based swapping needed

            // Direct mapping
            const buyerVol = Number(r.BuyerVol ?? 0);
            const buyerValue = Number(r.BuyerValue ?? 0);
            const sellerVol = Number(r.SellerVol ?? 0);
            const sellerValue = Number(r.SellerValue ?? 0);

            const netBuyVol = Number(r.NetBuyVol ?? 0);
            const netBuyValue = Number(r.NetBuyValue ?? 0);
            const netSellVol = Number(r.NetSellVol ?? 0);
            const netSellValue = Number(r.NetSellValue ?? 0);
            const netBuyerAvg = Number(r.NetBuyerAvg ?? 0);
            const netSellerAvg = Number(r.NetSellerAvg ?? 0);

            // Recalculate averages if 0 (handling frontend calculation request)
            // Helper to safe divide
            const safeDiv = (num: number, den: number) => den !== 0 ? num / den : 0;

            // Recalculate if avg is 0 but we have vol/value
            let finalBavg = Number(r.BuyerAvg ?? r.bavg ?? 0);
            if (finalBavg === 0 && Math.abs(buyerVol) > 0) finalBavg = safeDiv(buyerValue, buyerVol);

            let finalSavg = Number(r.SellerAvg ?? r.savg ?? 0);
            if (finalSavg === 0 && Math.abs(sellerVol) > 0) finalSavg = safeDiv(sellerValue, sellerVol);

            let finalNetBuyerAvg = netBuyerAvg;
            if (finalNetBuyerAvg === 0 && Math.abs(netBuyVol) > 0) finalNetBuyerAvg = safeDiv(netBuyValue, netBuyVol);

            let finalNetSellerAvg = netSellerAvg;
            if (finalNetSellerAvg === 0 && Math.abs(netSellVol) > 0) finalNetSellerAvg = safeDiv(netSellValue, netSellVol);

            return {
              broker: r.BrokerCode ?? r.broker ?? r.BROKER ?? r.code ?? '',
              buyerVol: buyerVol,
              buyerValue: buyerValue,
              bavg: finalBavg,
              sellerVol: sellerVol,
              sellerValue: sellerValue,
              savg: finalSavg,
              // Net Buy fields
              netBuyVol: netBuyVol,
              netBuyValue: netBuyValue,
              netBuyerAvg: finalNetBuyerAvg,
              // Net Sell fields
              netSellVol: netSellVol,
              netSellValue: netSellValue,
              netSellerAvg: finalNetSellerAvg,
              // Legacy fields for backward compatibility
              nblot: netBuyVol,
              nbval: netBuyValue,
              sl: Number(r.SellerVol ?? r.sl ?? 0),
              nslot: netSellVol,
              nsval: netSellValue
            };
          }) as BrokerSummaryData[];

          // Store in cache
          cache.set(cacheKey, { data: rows, timestamp: now });

          // Save to localStorage (debounced)
          saveCacheToStorage();

          return { ticker, date, data: rows };
        };

        // OPTIMIZED: Fetch all data in parallel with batching to avoid overwhelming browser
        // Batch size: Increased to 20 for faster sector loading (sectors have many stocks)
        // For sectors with 20+ stocks, this reduces batch count significantly
        const BATCH_SIZE = 20;

        // Create all fetch task descriptions (NOT promises yet - create promises only when needed)
        // Use uniqueTickers (sectors are fetched directly, not expanded)
        const allFetchTasks = selectedDates.flatMap(date =>
          uniqueTickers.map(ticker => ({ ticker, date }))
        );

        // Retry mechanism: try up to 3 times if no valid data fetched
        const MAX_RETRIES = 3;
        let validResults: Array<{ ticker: string; date: string; data: BrokerSummaryData[] }> = [];
        let validForeignResults: Array<{ ticker: string; date: string; data: BrokerSummaryData[] }> = [];
        let retryAttempt = 0;

        while (retryAttempt < MAX_RETRIES) {
          // CRITICAL: Check ref before each retry attempt
          if (!shouldFetchDataRef.current || abortController.signal.aborted) {
            setIsLoading(false);
            setIsDataReady(false);
            return;
          }

          const now = Date.now();

          // Clear expired cache entries
          for (const [key, value] of cache.entries()) {
            if (now - value.timestamp > CACHE_EXPIRY_MS) {
              cache.delete(key);
            }
          }

          const allDataResults: Array<{ ticker: string; date: string; data: BrokerSummaryData[] }> = [];
          const allForeignDataResults: Array<{ ticker: string; date: string; data: BrokerSummaryData[] }> = [];

          if (retryAttempt > 0) {
            // Add delay between retries (500ms)
            await new Promise(resolve => setTimeout(resolve, 500));
          }

          // OPTIMIZED: Process in batches with progressive rendering for faster display
          // For sectors with many stocks, show data as soon as first batch completes
          let hasShownFirstData = false;

          // Create promises only when needed (not all at once)
          for (let i = 0; i < allFetchTasks.length; i += BATCH_SIZE) {
            // CRITICAL: Check ref before each batch - user might have changed dates
            if (!shouldFetchDataRef.current || abortController.signal.aborted) {
              setIsLoading(false);
              setIsDataReady(false);
              return;
            }

            const batch = allFetchTasks.slice(i, i + BATCH_SIZE);

            try {
              // Create promises only for this batch (not all at once)
              // Skip cache on retry attempts (retryAttempt > 0)
              const batchPromises = batch.map(async ({ ticker, date }) => {
                // Main Fetch (based on user selection)
                const mainPromise = fetchSingleData(ticker, date, market, mainInvestorType, retryAttempt > 0);

                // Foreign Fetch for FNVal (using selected board 'market')
                // If main fetch IS ALREADY the same, reuse it
                let foreignPromise;
                if (mainInvestorType === 'F') {
                  // Reuse main promise result
                  foreignPromise = mainPromise.then(res => ({ ...res })); // Clone to avoid mutation issues
                } else {
                  foreignPromise = fetchSingleData(ticker, date, market, 'F', retryAttempt > 0);
                }

                const [mainRes, foreignRes] = await Promise.all([mainPromise, foreignPromise]);
                return { main: mainRes, foreign: foreignRes };
              });

              // Wait for current batch to complete
              const batchResults = await Promise.all(batchPromises);

              // CRITICAL: Check ref after each batch - user might have changed dates
              if (!shouldFetchDataRef.current || abortController.signal.aborted) {
                setIsLoading(false);
                setIsDataReady(false);
                return;
              }

              batchResults.forEach(res => {
                allDataResults.push(res.main);
                allForeignDataResults.push(res.foreign);
              });

              // PROGRESSIVE RENDERING: Show data after first batch completes (for faster display)
              // This is especially useful for sectors with many stocks
              if (!hasShownFirstData && allDataResults.length > 0) {
                const firstBatchValidResults = allDataResults.filter(result => result.ticker && result.date && result.data.length > 0);
                if (firstBatchValidResults.length > 0) {
                  // Aggregate and show first batch data immediately
                  const tempAggregatedMap = new Map<string, Map<string, BrokerSummaryData>>();
                  selectedDates.forEach(date => {
                    tempAggregatedMap.set(date, new Map<string, BrokerSummaryData>());
                  });

                  firstBatchValidResults.forEach(({ date, data }) => {
                    const dateMap = tempAggregatedMap.get(date);
                    if (!dateMap) return;

                    data.forEach(row => {
                      const broker = row.broker;
                      if (!broker) return;

                      const existing = dateMap.get(broker);
                      if (existing) {
                        existing.buyerVol += row.buyerVol;
                        existing.buyerValue += row.buyerValue;
                        existing.sellerVol += row.sellerVol;
                        existing.sellerValue += row.sellerValue;
                        existing.netBuyVol += row.netBuyVol;
                        existing.netBuyValue += row.netBuyValue;
                        existing.netSellVol += row.netSellVol;
                        existing.netSellValue += row.netSellValue;
                        existing.nblot += row.nblot;
                        existing.nbval += row.nbval;
                        existing.sl += row.sl;
                        existing.nslot += row.nslot;
                        existing.nsval += row.nsval;
                        existing.bavg = existing.buyerVol > 0 ? existing.buyerValue / existing.buyerVol : 0;
                        existing.savg = existing.sellerVol > 0 ? existing.sellerValue / existing.sellerVol : 0;
                        existing.netBuyerAvg = existing.netBuyVol > 0 ? existing.netBuyValue / existing.netBuyVol : 0;
                        existing.netSellerAvg = existing.netSellVol > 0 ? existing.netSellValue / existing.netSellVol : 0;
                      } else {
                        dateMap.set(broker, { ...row });
                      }
                    });
                  });

                  // Convert to final format (Map<string, BrokerSummaryData[]>) and show immediately
                  const tempFinalMap = new Map<string, BrokerSummaryData[]>();
                  tempAggregatedMap.forEach((brokerMap, date) => {
                    tempFinalMap.set(date, Array.from(brokerMap.values()));
                  });

                  setRawSummaryByDate(tempFinalMap);
                  setIsDataReady(true); // Show data immediately
                  hasShownFirstData = true;
                  console.log(`[BrokerSummary] Progressive rendering: Showing first batch data (${firstBatchValidResults.length} results)`);
                }
              }
            } catch (error: any) {
              // If aborted, silently abort
              if (error?.message === 'Fetch aborted' || !shouldFetchDataRef.current || abortController.signal.aborted) {
                setIsLoading(false);
                setIsDataReady(false);
                return;
              }

              // For other errors, log but continue with next batch
              console.error(`[BrokerSummary] Error in batch:`, error);
              // Don't add empty results - just skip failed items
            }
          }

          // CRITICAL: Final check after all batches - user might have changed dates during fetch
          if (!shouldFetchDataRef.current) {
            console.log('[BrokerSummary] fetchAll: shouldFetchDataRef became false after all batches, aborting aggregation');
            setIsLoading(false);
            setIsDataReady(false);
            return;
          }

          // Filter out empty results (failed fetches)
          validResults = allDataResults.filter(result => result.ticker && result.date && result.data.length > 0);
          validForeignResults = allForeignDataResults.filter(result => result.ticker && result.date && result.data.length > 0);

          console.log(`[BrokerSummary] Completed ${validResults.length}/${allFetchTasks.length} successful fetches (attempt ${retryAttempt + 1}/${MAX_RETRIES})`);

          // If we have valid results, break out of retry loop
          if (validResults.length > 0) {
            console.log(`[BrokerSummary] Successfully fetched data on attempt ${retryAttempt + 1}`);
            break;
          }

          // If no valid results and we haven't reached max retries, try again
          if (retryAttempt < MAX_RETRIES - 1) {
            console.warn(`[BrokerSummary] No valid data fetched on attempt ${retryAttempt + 1}, retrying...`);
            retryAttempt++;
          } else {
            // Last attempt failed, break and show error
            console.warn(`[BrokerSummary] No valid data fetched after ${MAX_RETRIES} attempts`);
            break;
          }
        }

        // If still no valid results after all retries, show "No Data Available" in table
        if (validResults.length === 0) {
          console.warn('[BrokerSummary] No valid data fetched after all retry attempts');
          // Clear data to show "No Data Available" in table
          setRawSummaryByDate(new Map());
          setSummaryByDate(new Map());
          setForeignSummaryByDate(new Map());
          setIsLoading(false);
          setIsDataReady(true); // Set to true so table can render "No Data Available"
          setError(null); // Clear error so table can render
          // Reset shouldFetchData to allow Show button to be clicked again
          shouldFetchDataRef.current = false;
          setShouldFetchData(false);
          return;
        }

        // Aggregate data per date and per broker (sum all tickers) - MAIN DATA
        const aggregatedMap = new Map<string, Map<string, BrokerSummaryData>>();
        // Also aggregate foreign data
        const aggregatedForeignMap = new Map<string, Map<string, BrokerSummaryData>>();

        selectedDates.forEach(date => {
          aggregatedMap.set(date, new Map<string, BrokerSummaryData>());
          aggregatedForeignMap.set(date, new Map<string, BrokerSummaryData>());
        });

        const aggregateResults = (results: typeof validResults, targetMap: typeof aggregatedMap) => {
          results.forEach(({ date, data }) => {
            const dateMap = targetMap.get(date);
            if (!dateMap) return;

            data.forEach(row => {
              const broker = row.broker;
              if (!broker) return;

              const existing = dateMap.get(broker);
              if (existing) {
                // Sum values from multiple tickers
                existing.buyerVol += row.buyerVol;
                existing.buyerValue += row.buyerValue;
                existing.sellerVol += row.sellerVol;
                existing.sellerValue += row.sellerValue;
                existing.netBuyVol += row.netBuyVol;
                existing.netBuyValue += row.netBuyValue;
                existing.netSellVol += row.netSellVol;
                existing.netSellValue += row.netSellValue;
                existing.nblot += row.nblot;
                existing.nbval += row.nbval;
                existing.sl += row.sl;
                existing.nslot += row.nslot;
                existing.nsval += row.nsval;

                // Recalculate averages
                const absBuyerVol = Math.abs(existing.buyerVol);
                existing.bavg = absBuyerVol > 0 ? existing.buyerValue / absBuyerVol : 0;
                const absSellerVol = Math.abs(existing.sellerVol);
                existing.savg = absSellerVol > 0 ? existing.sellerValue / absSellerVol : 0;
                const absNetBuyVol = Math.abs(existing.netBuyVol);
                existing.netBuyerAvg = absNetBuyVol > 0 ? existing.netBuyValue / absNetBuyVol : 0;
                const absNetSellVol = Math.abs(existing.netSellVol);
                existing.netSellerAvg = absNetSellVol > 0 ? existing.netSellValue / absNetSellVol : 0;
              } else {
                dateMap.set(broker, { ...row });
              }
            });
          });
        };

        // Aggregate both
        aggregateResults(validResults, aggregatedMap);
        aggregateResults(validForeignResults, aggregatedForeignMap);

        // CRITICAL: Check ref again before setting data - user might have changed dates during aggregation
        if (!shouldFetchDataRef.current || abortController.signal.aborted) {
          setIsLoading(false);
          setIsDataReady(false);
          return;
        }

        // Convert aggregated map to the format expected by the component
        const finalMap = new Map<string, BrokerSummaryData[]>();
        aggregatedMap.forEach((brokerMap, date) => {
          const rows = Array.from(brokerMap.values());
          finalMap.set(date, rows);
        });

        const finalForeignMap = new Map<string, BrokerSummaryData[]>();
        aggregatedForeignMap.forEach((brokerMap, date) => {
          const rows = Array.from(brokerMap.values());
          finalForeignMap.set(date, rows);
        });

        // CRITICAL: Final check before storing data - user might have changed dates
        if (!shouldFetchDataRef.current || abortController.signal.aborted) {
          setIsLoading(false);
          setIsDataReady(false);
          return;
        }

        // Store raw data (without investor filter) for client-side filtering
        setRawSummaryByDate(finalMap);
        setForeignSummaryByDate(finalForeignMap);

        // Note: summaryByDate will be updated by the filter effect below based on displayedFdFilter

        // CRITICAL: Check ref again before showing toast and setting data ready
        // User might have changed dates during aggregation
        if (!shouldFetchDataRef.current || abortController.signal.aborted) {
          setIsLoading(false);
          setIsDataReady(false);
          return;
        }

        // Show toast for range > 7 days only after data is fetched (after clicking Show)
        const showOnlyTotalNow = selectedDates.length > 7;
        if (showOnlyTotalNow) {
          showToast({
            type: 'info',
            title: 'Range Tanggal Lebih dari 7 Hari',
            message: `Anda telah memilih ${selectedDates.length} hari kerja. Hanya kolom Total yang akan ditampilkan untuk VALUE dan NET.`,
          });
        }

        // CRITICAL: Final check before marking data ready
        if (!shouldFetchDataRef.current || abortController.signal.aborted) {
          setIsLoading(false);
          setIsDataReady(false);
          return;
        }

        // Update displayed values when data is successfully fetched
        // This ensures header shows correct tickers and market after data is loaded
        setDisplayedTickers([...selectedTickers]);
        setDisplayedMarket(marketFilter);

        // Mark loading as complete and show data immediately
        setIsLoading(false);

        // OPTIMIZED: Show data immediately after state update
        // Column width sync will happen after data is visible (non-blocking)
        setIsDataReady(true);

        // CRITICAL: Reset shouldFetchData to false after successful fetch
        // This allows the Show button to be clicked again and trigger a new fetch
        shouldFetchDataRef.current = false;
        setShouldFetchData(false);

        // Clear abort controller after successful fetch
        abortControllerRef.current = null;
      } catch (e: any) {
        // If aborted, don't show error - just reset state
        // Check abortControllerRef.current instead of local abortController (which might be stale)
        const wasAborted = abortControllerRef.current?.signal.aborted || e?.message === 'Fetch aborted' || !shouldFetchDataRef.current;
        if (wasAborted) {
          setIsLoading(false);
          setIsDataReady(false);
          // Reset shouldFetchData to allow Show button to be clicked again
          shouldFetchDataRef.current = false;
          setShouldFetchData(false);
          abortControllerRef.current = null;
          return;
        }
        console.error('[BrokerSummary] Error fetching data:', e);
        setError(e?.message || 'Failed to load broker summary');
        setIsLoading(false);
        setIsDataReady(false);
        // Reset shouldFetchData to allow Show button to be clicked again
        shouldFetchDataRef.current = false;
        setShouldFetchData(false);
        abortControllerRef.current = null;
      }
    };

    fetchAll();

    // Cleanup: abort fetch if component unmounts or effect re-runs
    return () => {
      if (abortControllerRef.current) {
        console.log('[BrokerSummary] Cleaning up: aborting fetch');
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
    // Only depend on shouldFetchData - selectedTickers, selectedDates, marketFilter are already accessed inside
    // This prevents auto-fetch when dates/tickers change - only fetch when Show button is clicked
  }, [shouldFetchData]);

  // OPTIMIZED: Removed measureColumnWidths - width sync handled in syncTableWidths() which is more efficient

  // Sync NET container width with VALUE container width (tables auto-size, only sync container)
  useEffect(() => {
    let dataTimeoutId: NodeJS.Timeout | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let debounceTimer: NodeJS.Timeout | null = null;

    const syncTableWidths = () => {
      const valueContainer = valueTableContainerRef.current;
      const netContainer = netTableContainerRef.current;
      const valueTable = valueTableRef.current;
      const netTable = netTableRef.current;

      if (!valueContainer || !netContainer || !valueTable || !netTable) return;


      // STEP 1: Reset and prepare
      // Unlock layout to measure natural widths
      valueTable.style.tableLayout = 'auto';
      netTable.style.tableLayout = 'auto';

      // Reset containers and tables
      valueContainer.style.width = '';
      valueContainer.style.minWidth = '';
      netContainer.style.width = '';
      netContainer.style.minWidth = '';
      valueTable.style.width = '';
      netTable.style.width = '';

      const valueParentWrapper = valueContainer.parentElement;
      const netParentWrapper = netContainer.parentElement;
      if (valueParentWrapper && netParentWrapper) {
        valueParentWrapper.style.width = '';
        valueParentWrapper.style.minWidth = '';
        netParentWrapper.style.width = '';
        netParentWrapper.style.minWidth = '';
      }

      // STEP 2: Measure column widths across both tables
      const valueHeaderRows = valueTable.querySelectorAll('thead tr');
      const netHeaderRows = netTable.querySelectorAll('thead tr');

      if (valueHeaderRows.length < 2 || netHeaderRows.length < 2) {
        // Reset tableLayout so tables don't get stuck in 'auto' mode
        valueTable.style.tableLayout = 'fixed';
        netTable.style.tableLayout = 'fixed';
        return;
      }

      const valueColumnHeaderRow = valueHeaderRows[1];
      const netColumnHeaderRow = netHeaderRows[1];

      if (!valueColumnHeaderRow || !netColumnHeaderRow) return;

      const valueHeaderCells = Array.from(valueColumnHeaderRow.querySelectorAll('th'));
      const netHeaderCells = Array.from(netColumnHeaderRow.querySelectorAll('th'));
      const valueBodyRows = valueTable.querySelectorAll('tbody tr');
      const netBodyRows = netTable.querySelectorAll('tbody tr');

      const columnWidths: number[] = [];

      // Iterate over max of both tables' column counts so all columns are measured
      const totalCols = Math.max(valueHeaderCells.length, netHeaderCells.length);

      for (let index = 0; index < totalCols; index++) {
        const vCell = valueHeaderCells[index] as HTMLElement | undefined;
        const nCell = netHeaderCells[index] as HTMLElement | undefined;

        let maxWidth = Math.max(
          vCell ? vCell.scrollWidth : 0,
          vCell ? vCell.offsetWidth : 0,
          nCell ? nCell.scrollWidth : 0,
          nCell ? nCell.offsetWidth : 0
        );

        // Measure body cells from both tables for this column
        const checkBody = (rows: NodeListOf<Element>) => {
          rows.forEach(row => {
            const cells = row.querySelectorAll('td');
            if (cells[index]) {
              const el = cells[index] as HTMLElement;
              maxWidth = Math.max(maxWidth, el.scrollWidth, el.offsetWidth);
            }
          });
        };

        checkBody(valueBodyRows);
        checkBody(netBodyRows);

        columnWidths[index] = maxWidth;
      }

      // STEP 3: Apply exact fixed widths to both tables
      valueTable.style.tableLayout = 'fixed';
      netTable.style.tableLayout = 'fixed';

      const applyWidths = (headerCells: HTMLElement[], bodyRows: NodeListOf<Element>) => {
        headerCells.forEach((cell, index) => {
          const width = columnWidths[index];
          if (width !== undefined && width > 0) {
            cell.style.width = `${width}px`;
            cell.style.minWidth = `${width}px`;
            cell.style.maxWidth = `${width}px`;
          }
        });
        bodyRows.forEach(row => {
          const cells = row.querySelectorAll('td');
          cells.forEach((cell, index) => {
            const width = columnWidths[index];
            if (width !== undefined && width > 0) {
              const el = cell as HTMLElement;
              el.style.width = `${width}px`;
              el.style.minWidth = `${width}px`;
              el.style.maxWidth = `${width}px`;
            }
          });
        });
      };

      applyWidths(valueHeaderCells as HTMLElement[], valueBodyRows);
      applyWidths(netHeaderCells as HTMLElement[], netBodyRows);

      // STEP 4: For total-only mode (w-auto containers), sync widths to handle
      // vertical scrollbar width differences (on Windows, scrollbar takes ~17px horizontal space).
      // When one table has more rows and exceeds its maxHeight, it gets a vertical scrollbar
      // making its container ~17px wider than the other → visual misalignment.
      if (showOnlyTotal) {
        const vContainerWidth = valueContainer.offsetWidth;
        const nContainerWidth = netContainer.offsetWidth;
        const maxWidth = Math.max(vContainerWidth, nContainerWidth);
        if (maxWidth > 0 && vContainerWidth !== nContainerWidth) {
          valueContainer.style.width = `${maxWidth}px`;
          netContainer.style.width = `${maxWidth}px`;
        }
      }
    };

    const debouncedSync = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(syncTableWidths, 150);
    };

    if (valueTableRef.current && isDataReady) {
      resizeObserver = new ResizeObserver(debouncedSync);
      resizeObserver.observe(valueTableRef.current);
    }

    window.addEventListener('resize', debouncedSync);

    // Initial sync with multiple passes for certainty
    dataTimeoutId = setTimeout(syncTableWidths, 100);
    const retryTimeout = setTimeout(syncTableWidths, 500);

    return () => {
      if (dataTimeoutId) clearTimeout(dataTimeoutId);
      if (retryTimeout) clearTimeout(retryTimeout);
      if (debounceTimer) clearTimeout(debounceTimer);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', debouncedSync);
    };
  }, [summaryByDate, isLoading, isDataReady, showOnlyTotal]); // Re-sync whenever visible columns change


  // Synchronize horizontal scroll between Value, Net, and Total tables - optimized for smooth scrolling
  useEffect(() => {
    // Wait for tables to be ready (data loaded and DOM rendered)
    if (isLoading || !isDataReady) return;

    const valueContainer = valueTableContainerRef.current;
    const netContainer = netTableContainerRef.current;

    if (!valueContainer || !netContainer) return;

    // Synchronous scroll synchronization - no delay for real-time follow
    const syncScroll = (source: HTMLElement, targets: (HTMLElement | null)[]) => {
      const scrollLeft = source.scrollLeft;

      targets.forEach(target => {
        if (target && target.scrollLeft !== scrollLeft) {
          target.scrollLeft = scrollLeft;
        }
      });
    };

    const handleValueScroll = () => syncScroll(valueContainer, [netContainer]);
    const handleNetScroll = () => syncScroll(netContainer, [valueContainer]);

    valueContainer.addEventListener('scroll', handleValueScroll, { passive: true });
    netContainer.addEventListener('scroll', handleNetScroll, { passive: true });

    return () => {
      valueContainer.removeEventListener('scroll', handleValueScroll);
      netContainer.removeEventListener('scroll', handleNetScroll);
    };
  }, [isLoading, isDataReady, summaryByDate]); // Re-setup when data changes

  // clearAllDates removed with Reset button

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowStockSuggestions(false);
        setTickerInput('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (stockSearchTimeout) {
        clearTimeout(stockSearchTimeout);
      }
    };
  }, [stockSearchTimeout]);



  const handleStockSelect = (stock: string) => {
    if (disableTickerSelection) {
      // Ignore selection if ticker selection is disabled
      return;
    }
    // Check if it's a sector (has [SECTOR] prefix)
    if (stock.startsWith('[SECTOR] ')) {
      // Add sector name to selectedTickers (not individual stocks)
      if (!selectedTickers.includes(stock)) {
        const sectorName = stock.replace('[SECTOR] ', '');
        const stocksInSector = sectorMapping[sectorName] || [];

        // Remove individual stocks from this sector from selectedTickers (to avoid duplication)
        const updatedTickers = selectedTickers.filter(ticker =>
          !stocksInSector.includes(ticker)
        );

        setSelectedTickers([...updatedTickers, stock]);
        console.log(`[BrokerSummary] Added sector: ${sectorName} (${stocksInSector.length} stocks)`);
      }
    } else {
      // Regular stock selection - check if it's not already in a selected sector
      const stockSector = Object.keys(sectorMapping).find(sector =>
        sectorMapping[sector]?.includes(stock)
      );

      // Check if this stock's sector is already selected
      const sectorAlreadySelected = stockSector && selectedTickers.includes(`[SECTOR] ${stockSector}`);

      if (!sectorAlreadySelected && !selectedTickers.includes(stock)) {
        setSelectedTickers([...selectedTickers, stock]);
      }
    }
    setTickerInput('');
    // Don't close dropdown - allow multiple selections
  };

  const handleRemoveTicker = (stock: string) => {
    if (disableTickerSelection) {
      // Ignore removal if ticker selection is disabled
      return;
    }
    setSelectedTickers(selectedTickers.filter(t => t !== stock));
  };

  const handleClearAllTickers = () => {
    if (disableTickerSelection) {
      // Ignore clear if ticker selection is disabled
      return;
    }
    setSelectedTickers([]);
  };

  const handleStockInputChange = (value: string) => {
    setTickerInput(value);
    setShowStockSuggestions(true);

    // Clear previous timeout
    if (stockSearchTimeout) {
      clearTimeout(stockSearchTimeout);
      setStockSearchTimeout(null);
    }

    // If stocks not loaded yet, load them (no debounce needed - stocks already loaded on mount)
    // But if for some reason stocks are not loaded, try to load them
    if (availableStocks.length === 0) {
      const timeout = setTimeout(async () => {
        try {
          console.log('[BrokerSummary] Loading stock list on demand...');
          const [stockResult, sectorResult] = await Promise.all([
            api.getStockList(),
            fetchSectorMapping()
          ]);

          let stocks: string[] = [];
          let sectors: string[] = [];
          let mapping: { [sector: string]: string[] } = {};

          if (stockResult.success && stockResult.data?.stocks && Array.isArray(stockResult.data.stocks)) {
            stocks = stockResult.data.stocks;
          }

          if (sectorResult.success && sectorResult.data) {
            sectors = sectorResult.data.sectors || [];
            mapping = sectorResult.data.sectorMapping || {};
            setSectorMapping(mapping);
          }

          // Remove IDX from ticker list (IDX is now a sector, not a ticker)
          const stocksWithoutIdx = stocks.filter(stock => stock !== 'IDX');

          // Add sectors to the list (with prefix to distinguish from stocks)
          const sectorsWithPrefix = sectors.map(sector => `[SECTOR] ${sector}`);

          // Combine stocks and sectors, then sort alphabetically
          const allItems = [...stocksWithoutIdx, ...sectorsWithPrefix].sort((a: string, b: string) => a.localeCompare(b));

          setAvailableStocks(allItems);
        } catch (err) {
          console.error('Error loading stocks:', err);
          // Even if API fails, ensure IDX is available as sector
          setAvailableStocks(['[SECTOR] IDX']);
        }
      }, 100); // Short delay only if needed
      setStockSearchTimeout(timeout);
    }

    // (Auto-select exact match logic removed per user request, user must click explicitly)
  };

  // Separate stocks and sectors for display
  const filteredStocksList = (availableStocks || []).filter(stock => {
    const searchTerm = tickerInput.toLowerCase();
    // Only include stocks (not sectors)
    if (stock.startsWith('[SECTOR] ')) {
      return false;
    }
    // For regular stocks, search normally - don't filter out selected items
    return stock.toLowerCase().includes(searchTerm);
  });

  const filteredSectorsList = (availableStocks || []).filter(stock => {
    const searchTerm = tickerInput.toLowerCase();
    // Only include sectors - don't filter out selected items
    if (stock.startsWith('[SECTOR] ')) {
      const sectorName = stock.replace('[SECTOR] ', '').toLowerCase();
      return sectorName.includes(searchTerm);
    }
    return false;
  });



  // Helper function to format display name (remove [SECTOR] prefix for display)
  const formatStockDisplayName = (stock: string): string => {
    if (stock.startsWith('[SECTOR] ')) {
      const sectorName = stock.replace('[SECTOR] ', '');
      // Replace IDX with IDX Composite for display
      return sectorName === 'IDX' ? 'IDX Composite' : sectorName;
    }
    return stock;
  };

  // Helper function to trigger date picker
  const triggerDatePicker = (inputRef: React.RefObject<HTMLInputElement>) => {
    if (inputRef.current) {
      inputRef.current.showPicker();
    }
  };

  // Helper function to format date for input (YYYY-MM-DD)
  const formatDateForInput = (date: string) => {
    return date; // Already in YYYY-MM-DD format
  };

  // Font size fixed to normal (no menu)
  const getFontSizeClass = () => 'text-[12px]';

  // Filter raw data by investor filter (client-side filtering)
  useEffect(() => {
    if (rawSummaryByDate.size === 0) {
      // No raw data, clear filtered data
      setSummaryByDate(new Map());
      return;
    }

    // Apply investor filter to raw data
    const filteredMap = new Map<string, BrokerSummaryData[]>();

    rawSummaryByDate.forEach((rows, date) => {
      const filteredRows = rows.filter(() => {
        // Rely on backend data pre-filtered by investor type
        // No more client-side filtering based on broker code lists
        return true;
      });

      if (filteredRows.length > 0) {
        filteredMap.set(date, filteredRows);
      }
    });

    setSummaryByDate(filteredMap);
  }, [rawSummaryByDate, displayedFdFilter]);

  // Helper function to filter brokers by F/D (uses displayedFdFilter - the filter that was applied when Show button was clicked)
  const brokerFDScreen = (_broker?: string): boolean => {
    // Rely on backend data pre-filtered by investor type
    // No more client-side filtering based on broker code lists
    return true;
  };



  // Helper function to get market/board label for display
  const getMarketLabel = (market: 'RG' | 'TN' | 'NG' | ''): string => {
    if (market === 'RG') return 'RG';
    if (market === 'TN') return 'TN';
    if (market === 'NG') return 'NG';
    return 'All';
  };

  // Memoize availableDates to avoid recalculating on every render
  // CRITICAL: Only depend on summaryByDate, not selectedDates
  // This ensures NO recalculate when user changes dates - only when data changes (after Show button clicked)
  const availableDates = useMemo(() => {
    // Return dates that have data in summaryByDate, sorted by date
    const datesWithData = Array.from(summaryByDate.keys()).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    return datesWithData;
  }, [summaryByDate]);


  // Memoize allBrokerData to avoid recalculating on every render
  const allBrokerData = useMemo(() => {
    return availableDates.map(date => {
      const rows = summaryByDate.get(date) || [];
      return {
        date,
        buyData: rows,
        sellData: rows
      };
    });
  }, [availableDates, summaryByDate]);

  // OPTIMIZED: Memoize sorted data per date for VALUE table
  const sortedValueDataByDateMemo = useMemo(() => {
    const map = new Map<string, { buy: any[], sell: any[] }>();
    availableDates.forEach(date => {
      const rows = summaryByDate.get(date) || [];
      const buy = rows
        .filter(b => brokerFDScreen(b.broker) && b.buyerValue > 0)
        .sort((a, b) => b.buyerValue - a.buyerValue);
      const sell = rows
        .filter(s => brokerFDScreen(s.broker) && s.sellerValue > 0)
        .sort((a, b) => b.sellerValue - a.sellerValue);
      map.set(date, { buy, sell });
    });
    return map;
  }, [availableDates, summaryByDate, displayedFdFilter]);

  // OPTIMIZED: Memoize total data for VALUE table
  const totalValueDataMemo = useMemo(() => {
    const totalBuyData: { [broker: string]: { nblot: number; nbval: number; bavg: number; count: number } } = {};
    const totalSellData: { [broker: string]: { nslot: number; nsval: number; savg: number; count: number } } = {};

    allBrokerData.forEach(dateData => {
      dateData.buyData.forEach(b => {
        if (b.buyerValue > 0) {
          if (!totalBuyData[b.broker]) totalBuyData[b.broker] = { nblot: 0, nbval: 0, bavg: 0, count: 0 };
          const entry = totalBuyData[b.broker]!;
          entry.nblot += b.buyerVol;
          entry.nbval += b.buyerValue;
          entry.bavg += b.bavg;
          entry.count += 1;
        }
      });
      dateData.sellData.forEach(s => {
        if (s.sellerValue > 0) {
          if (!totalSellData[s.broker]) totalSellData[s.broker] = { nslot: 0, nsval: 0, savg: 0, count: 0 };
          const entry = totalSellData[s.broker]!;
          entry.nslot += s.sellerVol;
          entry.nsval += s.sellerValue;
          entry.savg += s.savg;
          entry.count += 1;
        }
      });
    });

    const buy = Object.entries(totalBuyData)
      .filter(([broker]) => brokerFDScreen(broker))
      .map(([broker, data]) => ({ broker, ...data, bavg: data.bavg / data.count }))
      .sort((a, b) => b.nbval - a.nbval);

    const sell = Object.entries(totalSellData)
      .filter(([broker]) => brokerFDScreen(broker))
      .map(([broker, data]) => ({ broker, ...data, savg: data.savg / data.count }))
      .sort((a, b) => b.nsval - a.nsval);

    return { buy, sell };
  }, [allBrokerData, displayedFdFilter]);

  // OPTIMIZED: Memoize sorting for NET table
  const sortedNetDataByDateMemo = useMemo(() => {
    const map = new Map<string, { buy: any[], sell: any[] }>();
    availableDates.forEach(date => {
      const rows = summaryByDate.get(date) || [];
      const buy = rows
        .filter(b => brokerFDScreen(b.broker) && (b.netBuyVol > 0 || b.netBuyValue > 0) && (b.netBuyValue >= b.netSellValue))
        .sort((a, b) => (b.netBuyValue || 0) - (a.netBuyValue || 0));
      const sell = rows
        .filter(b => brokerFDScreen(b.broker) && (b.netSellVol > 0 || b.netSellValue > 0) && (b.netSellValue > b.netBuyValue))
        .sort((a, b) => (b.netSellValue || 0) - (a.netSellValue || 0));
      map.set(date, { buy, sell });
    });
    return map;
  }, [availableDates, summaryByDate, displayedFdFilter]);

  // OPTIMIZED: Memoize total data for NET table
  const totalNetDataMemo = useMemo(() => {
    // Standard 1:1 Mapping - No Swapping
    // User confirmed: "ga ada yang di swap swap"

    // Accumulators for REAL Net Buy and REAL Net Sell
    const brokerRealNetBuyTotals: { [broker: string]: { nblot: number; nbval: number; avgSum: number; avgCount: number } } = {};
    const brokerRealNetSellTotals: { [broker: string]: { nslot: number; nsval: number; avgSum: number; avgCount: number } } = {};

    allBrokerData.forEach(dateData => {
      // Use buyData to iterate rows (contains complete brokerage data)
      dateData.buyData.forEach(b => {
        // Direct mapping: Net Buy is Net Buy, Net Sell is Net Sell
        const realNetBuyVol = b.netBuyVol || 0;
        const realNetBuyVal = b.netBuyValue || 0;
        const realNetBuyAvg = b.netBuyerAvg || 0;

        const realNetSellVol = b.netSellVol || 0;
        const realNetSellVal = b.netSellValue || 0;
        const realNetSellAvg = b.netSellerAvg || 0;

        // Accumulate Real Net Buy
        if (realNetBuyVol > 0 || realNetBuyVal > 0) {
          if (!brokerRealNetBuyTotals[b.broker]) brokerRealNetBuyTotals[b.broker] = { nblot: 0, nbval: 0, avgSum: 0, avgCount: 0 };
          const entry = brokerRealNetBuyTotals[b.broker]!;
          entry.nblot += realNetBuyVol;
          entry.nbval += realNetBuyVal;
          if (realNetBuyAvg) { entry.avgSum += realNetBuyAvg; entry.avgCount++; }
        }

        // Accumulate Real Net Sell
        if (realNetSellVol > 0 || realNetSellVal > 0) {
          if (!brokerRealNetSellTotals[b.broker]) brokerRealNetSellTotals[b.broker] = { nslot: 0, nsval: 0, avgSum: 0, avgCount: 0 };
          const entry = brokerRealNetSellTotals[b.broker]!;
          entry.nslot += realNetSellVol;
          entry.nsval += realNetSellVal;
          if (realNetSellAvg) { entry.avgSum += realNetSellAvg; entry.avgCount++; }
        }
      });
    });

    const finalRealNetBuyList: any[] = [];
    const finalRealNetSellList: any[] = [];

    const allBrokers = new Set([...Object.keys(brokerRealNetBuyTotals), ...Object.keys(brokerRealNetSellTotals)]);
    allBrokers.forEach(broker => {
      if (!brokerFDScreen(broker)) return;

      const bTotal = brokerRealNetBuyTotals[broker] || { nblot: 0, nbval: 0, avgSum: 0, avgCount: 0 };
      const sTotal = brokerRealNetSellTotals[broker] || { nslot: 0, nsval: 0, avgSum: 0, avgCount: 0 };

      let bavg = bTotal.avgCount > 0 ? bTotal.avgSum / bTotal.avgCount : 0;
      let savg = sTotal.avgCount > 0 ? sTotal.avgSum / sTotal.avgCount : 0;

      // Fallback avg calculation if count is 0 (shouldn't happen for valid data but good for safety)
      // Actually previous code calculated avg from total Val/Lot if avg was 0.
      if (bavg === 0 && bTotal.nblot > 0) bavg = bTotal.nbval / bTotal.nblot;
      if (savg === 0 && sTotal.nslot > 0) savg = Math.abs(sTotal.nsval) / sTotal.nslot;

      // Netting Logic (Real Buy vs Real Sell)
      // User Request: "Prioritaskan Value sebagai acuan utama"
      const netValue = bTotal.nbval - sTotal.nsval;
      const netLot = bTotal.nblot - sTotal.nslot;

      if (netValue > 0) {
        // Net Buyer (Positive Net Value)
        finalRealNetBuyList.push({
          broker,
          nblot: netLot,
          nbval: netValue,
          nbavg: bavg,
          bavg: bavg
        });
      } else if (netValue < 0) {
        // Net Seller (Negative Net Value -> Positive Net Sell Value)
        finalRealNetSellList.push({
          broker,
          nslot: -netLot, // Invert for display (Net Sell Lot matches Net Sell Value direction usually)
          nsval: -netValue, // Invert for positive display value
          savg: savg
        });
      } else {
        // Tie-breaker: Value is 0 (Equal Buy/Sell Value). Check Volume/Lot.
        if (netLot > 0) {
          // Net Buyer by Volume
          finalRealNetBuyList.push({ broker, nblot: netLot, nbval: 0, nbavg: bavg, bavg: bavg });
        } else if (netLot < 0) {
          // Net Seller by Volume
          finalRealNetSellList.push({ broker, nslot: -netLot, nsval: 0, savg: savg });
        }
      }
    });

    const sortedRealNetBuy = finalRealNetBuyList.filter(i => i.nbval > 0).sort((a, b) => b.nbval - a.nbval);
    const sortedRealNetSell = finalRealNetSellList.filter(i => i.nsval > 0).sort((a, b) => b.nsval - a.nsval);

    // MAPPING TO OUTPUT
    // consistently return Real Net Buy in 'buy' and Real Net Sell in 'sell'
    // The component's render logic handles the column swapping (Left/Right placement)
    return {
      buy: sortedRealNetBuy,
      sell: sortedRealNetSell
    };

  }, [allBrokerData, displayedFdFilter]);

  // Fetch OHLC for AI Level 1 Smart Money calculation
  useEffect(() => {
    // Only fetch if popup is open, we have a selected ticker, and available dates
    if (!showAIPopup || selectedTickers.length === 0 || availableDates.length === 0) return;
    
    const fetchOhlc = async () => {
      try {
        const tickerRaw = selectedTickers[0];
        // Skip for index/sectors
        if (tickerRaw === 'IDX' || tickerRaw.startsWith('[SECTOR]')) {
          setClosePriceForAI(null); 
          return;
        }
        
        const ticker = tickerRaw;
        
        // Find sector by iterating sectorMapping
        let sector = '';
        for (const [s, stocks] of Object.entries(sectorMapping)) {
          if (stocks.includes(ticker)) {
            sector = s;
            break;
          }
        }
        
        if (!sector) {
          console.warn(`[BrokerSummary AI] No sector found for ticker ${ticker}`);
          return;
        }

        const tokenRes = await api.getBrokerSummarySasToken();
        if (!tokenRes.success || !tokenRes.data) {
          console.warn(`[BrokerSummary AI] Failed to get SAS token`);
          return;
        }
        
        const { containerUrl, sasToken } = tokenRes.data;
        const blobPath = `ohlc/stocks/${sector}/${ticker}.parquet`;
        
        const { loadOhlcByPath } = await import('../../services/duckdbService');
        const rows = await loadOhlcByPath(blobPath, containerUrl, sasToken);
        
        if (!rows || rows.length === 0) return;

        // the dates in availableDates are like "YYYY-MM-DD"
        const targetDate = availableDates[availableDates.length - 1];
        
        let match = rows.find(r => {
          const rDate = r.Date || r.date || r.time || r.DateStr;
          if (rDate instanceof Date) {
            return rDate.toISOString().startsWith(targetDate);
          } else if (typeof rDate === 'string') {
            return rDate.startsWith(targetDate);
          }
          return false;
        });

        // fallback to most recent date <= targetDate
        if (!match) {
           const targetTime = new Date(targetDate).getTime();
           let bestMatch = rows[0];
           let bestTimeDiff = Infinity;
           for (const r of rows) {
             const rDate = r.Date || r.date || r.time || r.DateStr;
             let time = 0;
             if (rDate instanceof Date) time = rDate.getTime();
             else if (typeof rDate === 'string') time = new Date(rDate).getTime();
             
             if (time && time <= targetTime) {
               const diff = targetTime - time;
               if (diff >= 0 && diff < bestTimeDiff) {
                 bestTimeDiff = diff;
                 bestMatch = r;
               }
             }
           }
           match = bestTimeDiff !== Infinity ? bestMatch : rows[rows.length - 1];
        }

        if (match) {
           const closeVal = match.Close !== undefined ? match.Close : match.close;
           setClosePriceForAI(Number(closeVal));
        }

      } catch (err) {
        console.error(`[BrokerSummary AI] Error fetching OHLC data for Smart Money`, err);
      }
    };

    fetchOhlc();
  }, [showAIPopup, selectedTickers, availableDates, sectorMapping]);

  // AI Level 1 logic
  const aiLevel1Stats = useMemo(() => {
    if (!totalNetDataMemo || totalNetDataMemo.buy.length === 0 || !totalValueDataMemo) return [];
    
    // 1. Get union of top 5 net buy + top 5 gross buy (dedup) - gross yang ada di net sell ga usah ditampilkan
    const netTop5 = totalNetDataMemo.buy.slice(0, 5).map(b => b.broker);
    const grossTop5 = totalValueDataMemo.buy.slice(0, 5).map(b => b.broker);
    const netSellSet = new Set(totalNetDataMemo.sell.map(b => b.broker));
    const filteredGrossTop5 = grossTop5.filter(b => !netSellSet.has(b));
    const top5Brokers = Array.from(new Set([...netTop5, ...filteredGrossTop5]));
    
    // Compute grand market totals
    let marketTVal = 0;
    let marketTLotShares = 0;
    totalValueDataMemo.buy.forEach(b => {
      marketTVal += b.nbval;
      marketTLotShares += b.nblot; // buyerVol is in shares
    });
    const marketTLot = marketTLotShares / 100; // Total Lot
    
    const stats: any[] = [];

    top5Brokers.forEach(broker => {
      const netData = totalNetDataMemo.buy.find(b => b.broker === broker) || null;
      const grossBuyData = totalValueDataMemo.buy.find(b => b.broker === broker);
      const grossSellData = totalValueDataMemo.sell.find(s => s.broker === broker);
      
      if (!grossBuyData) return;
      // For gross-only brokers that are net sellers, netBuy is 0
      const netBuyLot = netData ? netData.nblot / 100 : 0;
      const grossBuyLot = grossBuyData.nblot / 100;
      
      // Accumulation: lot buy net / lot buy gross (%)
      const accumulation = grossBuyLot > 0 ? (netBuyLot / grossBuyLot) * 100 : 0;
      
      // Consistency: top 10 buy per day (%)
      let daysInTop10 = 0;
      availableDates.forEach(date => {
        const dailyNetData = sortedNetDataByDateMemo.get(date);
        if (dailyNetData) {
          const top10Daily = dailyNetData.buy.slice(0, 10).map(b => b.broker);
          if (top10Daily.includes(broker)) {
            daysInTop10++;
          }
        }
      });
      const consistency = availableDates.length > 0 ? (daysInTop10 / availableDates.length) * 100 : 0;

      // Buy streak: consecutive Top-10 BUY days from the most recent date backwards
      // Untuk kasus "3 hari jual lalu 2 hari beli runtut" -> buyStreak=2 tapi consistency=40% (2/5)
      // Kita tandai broker tsb dengan bintang * sebagai late-starter/baru mulai akum
      let buyStreak = 0;
      for (let i = availableDates.length - 1; i >= 0; i--) {
        const d = availableDates[i]!;
        const daily = sortedNetDataByDateMemo.get(d);
        if (!daily) break;
        const top10 = daily.buy.slice(0, 10).map(b => b.broker);
        if (top10.includes(broker)) buyStreak++;
        else break;
      }
      // Any-buy streak (lebih longgar: sekadar net buyer, tidak harus Top 10) — info tambahan untuk tooltip
      let anyBuyStreak = 0;
      for (let i = availableDates.length - 1; i >= 0; i--) {
        const d = availableDates[i]!;
        const daily = sortedNetDataByDateMemo.get(d);
        if (!daily) break;
        const anyBuy = daily.buy.map(b => b.broker);
        if (anyBuy.includes(broker)) anyBuyStreak++;
        else break;
      }
      
      // Domination: net buy lot / tlot market (%)
      const domination = marketTLot > 0 ? (netBuyLot / marketTLot) * 100 : 0;
      
      // Big Money: (buy gross val + sell gross val) / (tval * 2) (%)
      const brokerGrossBuyVal = grossBuyData.nbval;
      const brokerGrossSellVal = grossSellData ? grossSellData.nsval : 0;
      const bigMoney = marketTVal > 0 ? ((brokerGrossBuyVal + brokerGrossSellVal) / (marketTVal * 2)) * 100 : 0;
      
      // Smart Money: (1 - (avg buy / close price)) * 100
      const avgBuy = netData && netData.nblot && Math.abs(netData.nblot) > 0 
        ? netData.nbval / Math.abs(netData.nblot) 
        : (netData?.nbavg || 0);
      
      const smartMoney = closePriceForAI && closePriceForAI > 0 && avgBuy > 0
        ? (1 - (avgBuy / closePriceForAI)) * 100 
        : 0;

      // Floating (Rp): nominal unrealized PnL = (close - avgBuy) * netBuy shares
      const floatingNominal = closePriceForAI && closePriceForAI > 0 && avgBuy > 0 && netData
        ? (closePriceForAI - avgBuy) * Math.abs(netData.nblot)
        : 0;
      
      stats.push({
        broker,
        accumulation: Math.round(accumulation).toString() + '%',
        accumulationRaw: accumulation,
        consistency: Math.round(consistency).toString() + '%',
        consistencyRaw: consistency,
        daysInTop10,
        totalDays: availableDates.length,
        domination: domination.toFixed(1) + '%',
        bigMoney: bigMoney.toFixed(1) + '%',
        smartMoney: (closePriceForAI && avgBuy > 0 ? smartMoney.toFixed(1) + '%' : '-'),
        rawSmartMoney: smartMoney,
        floating: (closePriceForAI && avgBuy > 0 ? formatNumber(floatingNominal) : '-'),
        rawFloating: floatingNominal,
        buyStreak,
        anyBuyStreak,
        isLateStarter: buyStreak >= 2 && buyStreak < availableDates.length
      });
    });
    
    return stats;
  }, [totalNetDataMemo, totalValueDataMemo, availableDates, sortedNetDataByDateMemo, closePriceForAI]);

  const smartMoneyBroker = useMemo(() => {
    if (aiLevel1Stats.length === 0) return null;
    const validStats = aiLevel1Stats.filter(s => s.rawSmartMoney > 0);
    if (validStats.length === 0) return null;
    return [...validStats].sort((a, b) => a.rawSmartMoney - b.rawSmartMoney)[0]?.broker;
  }, [aiLevel1Stats]);

  const renderHorizontalView = () => {
    if (selectedTickers.length === 0 || selectedDates.length === 0) return null;

    // Show error state
    if (error) {
      return (
        <div className="w-full px-4 py-8">
          <div className="text-sm text-destructive px-4 py-2 bg-destructive/10 rounded-md">
            {error}
          </div>
        </div>
      );
    }

    const datesForHeader = summaryByDate.size === 0 ? selectedDates : availableDates;
    const netLabel = displayedFdFilter === 'Domestic' ? 'DNVal' : 'FNVal';

    return (
      <div className="w-full h-full flex flex-col relative min-h-0 pb-2">
        {/* Tables */}
        <div className="w-full flex-1 flex flex-col min-h-0">
          {/* Combined Buy & Sell Side Table */}
          <div className="w-full max-w-full flex-1 flex flex-col min-h-0">
            <div className="bg-muted/50 px-4 py-1.5 border-y border-border">
              <h3 className="font-semibold text-sm">VALUE - {displayedTickers.map(t => formatStockDisplayName(t)).join(', ')} - {getMarketLabel(displayedMarket)}</h3>
            </div>
            <div className={`${showOnlyTotal ? 'flex justify-center' : 'w-full max-w-full'} flex-1 min-h-0`}>
              <div ref={valueTableContainerRef} className={`${showOnlyTotal ? 'w-auto' : 'w-full max-w-full'} ${summaryByDate.size === 0 ? 'overflow-hidden' : 'overflow-x-auto overflow-y-auto'} border-l-2 border-r-2 border-b-2 border-white h-full`} style={{ scrollBehavior: 'auto' }}>
                <table ref={valueTableRef} className={`${showOnlyTotal ? 'min-w-0' : summaryByDate.size === 0 ? 'w-full' : 'min-w-[1000px]'} ${getFontSizeClass()}`} style={{ tableLayout: summaryByDate.size === 0 ? 'fixed' : (isDataReady ? 'fixed' : 'auto'), width: summaryByDate.size === 0 ? '100%' : (showOnlyTotal ? 'auto' : undefined) }}>
                  <thead className="bg-[#3a4252] sticky top-0 z-10">
                    <tr className="border-t-2 border-white">
                      {!showOnlyTotal && datesForHeader.map((date, dateIndex) => (
                        <th key={date} className={`text-center py-[1px] px-[7.4px] font-bold text-white whitespace-nowrap ${dateIndex === 0 ? 'border-l-2 border-white' : ''} ${dateIndex < datesForHeader.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === datesForHeader.length - 1 ? 'border-r-[10px] border-white' : ''}`} colSpan={9} style={{ textAlign: 'center' }}>
                          {formatDisplayDate(date)}
                        </th>
                      ))}
                      <th className={`text-center py-[1px] px-[3.8px] font-bold text-white border-r-2 border-white ${showOnlyTotal || datesForHeader.length === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white'}`} colSpan={9} style={{ textAlign: 'center' }}>
                        Total
                      </th>
                    </tr>
                    <tr className="bg-[#3a4252]">
                      {!showOnlyTotal && datesForHeader.map((date, dateIndex) => (
                        <React.Fragment key={`detail-${date}`}>
                          {/* BY Columns */}
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white w-4 ${dateIndex === 0 ? 'border-l-2 border-white' : ''}`} style={{ textAlign: 'center' }}>BY</th>
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white w-6`} style={{ textAlign: 'center' }}>BLot</th>
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white w-6`} style={{ textAlign: 'center' }}>BVal</th>
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white w-6`} style={{ textAlign: 'center' }}>BAvg</th>
                          {/* SL Columns */}
                          <th className="text-center py-[1px] px-[5.4px] font-bold text-white bg-[#3a4252] w-4" style={{ textAlign: 'center' }}>#</th>
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white w-4`} style={{ textAlign: 'center' }}>SL</th>
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white w-6`} style={{ textAlign: 'center' }}>SLot</th>
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white w-6`} style={{ textAlign: 'center' }}>SVal</th>
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white w-6 ${dateIndex < datesForHeader.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === datesForHeader.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ textAlign: 'center' }}>SAvg</th>
                        </React.Fragment>
                      ))}
                      {/* Total Columns - Include BAvg and SAvg */}
                      <th className={`text-center py-[1px] px-[4.5px] font-bold text-white ${showOnlyTotal || datesForHeader.length === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white'}`} style={{ textAlign: 'center' }}>BY</th>
                      <th className={`text-center py-[1px] px-[4.5px] font-bold text-white`} style={{ textAlign: 'center' }}>BLot</th>
                      <th className={`text-center py-[1px] px-[4.5px] font-bold text-white`} style={{ textAlign: 'center' }}>BVal</th>
                      <th className={`text-center py-[1px] px-[4.5px] font-bold text-white`} style={{ textAlign: 'center' }}>BAvg</th>
                      <th className="text-center py-[1px] px-[5.4px] font-bold text-white bg-[#3a4252]" style={{ textAlign: 'center' }}>#</th>
                      <th className={`text-center py-[1px] px-[4.5px] font-bold text-white`} style={{ textAlign: 'center' }}>SL</th>
                      <th className={`text-center py-[1px] px-[4.5px] font-bold text-white`} style={{ textAlign: 'center' }}>SLot</th>
                      <th className={`text-center py-[1px] px-[4.5px] font-bold text-white`} style={{ textAlign: 'center' }}>SVal</th>
                      <th className={`text-center py-[1px] px-[6.3px] font-bold text-white border-r-2 border-white`} style={{ textAlign: 'center' }}>SAvg</th>
                    </tr>
                  </thead>
                  <tbody className="text-[12px]">
                    {(() => {
                      // Get broker text color class
                      const getBrokerColorClass = (brokerCode: string): string => {
                        if (GOVERNMENT_BROKERS.includes(brokerCode)) {
                          return 'text-green-600 font-semibold';
                        }
                        if (FOREIGN_BROKERS.includes(brokerCode)) {
                          return 'text-red-600 font-semibold';
                        }
                        return 'text-white font-semibold';
                      };

                      // OPTIMIZED: Use memoized total data
                      const sortedTotalBuy = totalValueDataMemo.buy;
                      const sortedTotalSell = totalValueDataMemo.sell;

                      // Find max row count across all dates AND total columns
                      let maxRows = 0;
                      availableDates.forEach(date => {
                        const sortedData = sortedValueDataByDateMemo.get(date);
                        const buyCount = sortedData?.buy.length || 0;
                        const sellCount = sortedData?.sell.length || 0;
                        maxRows = Math.max(maxRows, buyCount, sellCount);
                      });

                      // Also include total broker counts in maxRows (for Total column)
                      const totalBuyCount = sortedTotalBuy.length;
                      const totalSellCount = sortedTotalSell.length;
                      maxRows = Math.max(maxRows, totalBuyCount, totalSellCount);

                      // Ensure minimum 21 rows for consistent display (20 visible + 1 to avoid scrollbar covering row 20)
                      maxRows = Math.max(maxRows, 21);

                      // If no data, show "No Data Available" message
                      if (summaryByDate.size === 0) {
                        const totalCols = datesForHeader.length * 9 + 9; // 9 cols per date + 9 for Total
                        return (
                          <tr className="border-b-2 border-white">
                            <td
                              colSpan={totalCols}
                              className="text-center py-[2.06px] text-muted-foreground font-bold"
                              style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
                            >
                              No Data Available
                            </td>
                          </tr>
                        );
                      }

                      // Render all rows (scrollable container handles overflow)
                      return Array.from({ length: maxRows }).map((_, rowIdx) => (
                        <tr key={rowIdx} className={`hover:bg-accent/50 ${rowIdx === maxRows - 1 ? 'border-b-2 border-white' : ''}`}>
                          {!showOnlyTotal && availableDates.map((date, dateIndex) => {
                            // OPTIMIZED: Use memoized sorted data
                            const sortedRowData = sortedValueDataByDateMemo.get(date);
                            const buyData = sortedRowData?.buy[rowIdx];
                            const sellData = sortedRowData?.sell[rowIdx];

                            return (
                              <React.Fragment key={`${date}-${rowIdx}`}>
                                {/* BY (Buyer) Columns - Using Buyer fields */}
                                <td className={`text-center py-[1px] px-[5.4px] w-4 font-bold ${dateIndex === 0 ? 'border-l-2 border-white' : ''} ${buyData ? getBrokerColorClass(buyData.broker) : ''}`}>
                                  {buyData?.broker || '-'}
                                </td>
                                <td className="text-right py-[1px] px-[5.4px] text-green-600 font-bold w-6" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {buyData ? formatLot(buyData.buyerVol / 100) : '-'}
                                </td>
                                <td className="text-right py-[1px] px-[5.4px] text-green-600 font-bold w-6" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {buyData ? formatNumber(buyData.buyerValue) : '-'}
                                </td>
                                <td className="text-right py-[1px] px-[5.4px] text-green-600 font-bold w-6" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {buyData ? formatAverage(buyData.bavg) : '-'}
                                </td>
                                {/* SL (Seller) Columns - Keep # column */}
                                <td className={`text-center py-[1px] px-[5.4px] text-white bg-[#3a4252] font-bold w-4 ${sellData ? getBrokerColorClass(sellData.broker) : ''}`}>{sellData ? rowIdx + 1 : '-'}</td>
                                <td className={`py-[1px] px-[5.4px] font-bold w-4 ${sellData ? getBrokerColorClass(sellData.broker) : ''}`}>
                                  {sellData?.broker || '-'}
                                </td>
                                <td className="text-right py-[1px] px-[5.4px] text-red-600 font-bold w-6" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {sellData ? formatLot(sellData.sellerVol / 100) : '-'}
                                </td>
                                <td className="text-right py-[1px] px-[5.4px] text-red-600 font-bold w-6" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {sellData ? formatNumber(sellData.sellerValue) : '-'}
                                </td>
                                <td className={`text-right py-[1px] px-[5.4px] text-red-600 font-bold w-6 ${dateIndex < availableDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === availableDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {sellData ? formatAverage(sellData.savg) : '-'}
                                </td>
                              </React.Fragment>
                            );
                          })}
                          {/* Total Columns - Include BAvg and SAvg */}
                          {(() => {
                            const totalBuy = sortedTotalBuy[rowIdx];
                            const totalSell = sortedTotalSell[rowIdx];
                            // Calculate BAvg: BVal / BLot
                            const totalBuyAvg = totalBuy && Math.abs(totalBuy.nblot) > 0 ? totalBuy.nbval / Math.abs(totalBuy.nblot) : 0;
                            // Calculate SAvg: SVal / SLot
                            const totalSellAvg = totalSell && Math.abs(totalSell.nslot) > 0 ? Math.abs(totalSell.nsval) / Math.abs(totalSell.nslot) : 0;
                            return (
                              <React.Fragment>
                                <td className={`text-center py-[1px] px-[4.5px] font-bold ${showOnlyTotal || datesForHeader.length === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white'} ${totalBuy ? getBrokerColorClass(totalBuy.broker) : ''}`}>
                                  {totalBuy?.broker || '-'}
                                </td>
                                <td className="text-right py-[1px] px-[1.5px] text-green-600 font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {totalBuy ? formatLot(totalBuy.nblot / 100) : '-'}
                                </td>
                                <td className="text-right py-[1px] px-[1.5px] text-green-600 font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {totalBuy ? formatNumber(totalBuy.nbval) : '-'}
                                </td>
                                <td className="text-right py-[1px] px-[4.5px] text-green-600 font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {totalBuy && totalBuyAvg > 0 ? formatAverage(totalBuyAvg) : '-'}
                                </td>
                                <td className={`text-center py-[1px] px-[5.4px] text-white bg-[#3a4252] font-bold ${totalSell ? getBrokerColorClass(totalSell.broker) : ''}`}>{totalSell ? rowIdx + 1 : '-'}</td>
                                <td className={`py-[1px] px-[4.5px] font-bold ${totalSell ? getBrokerColorClass(totalSell.broker) : ''}`}>
                                  {totalSell?.broker || '-'}
                                </td>
                                <td className="text-right py-[1px] px-[1.5px] text-red-600 font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {totalSell ? formatLot(totalSell.nslot / 100) : '-'}
                                </td>
                                <td className="text-right py-[1px] px-[1.5px] text-red-600 font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {totalSell ? formatNumber(totalSell.nsval) : '-'}
                                </td>
                                <td className="text-right py-[1px] px-[6.3px] text-red-600 font-bold border-r-2 border-white" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {totalSell && totalSellAvg > 0 ? formatAverage(totalSellAvg) : '-'}
                                </td>
                              </React.Fragment>
                            );
                          })()}
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Net Table */}
          <div className="w-full max-w-full flex-1 flex flex-col min-h-0 mt-1">
            <div className="bg-muted/50 px-4 py-1.5 border-y border-border">
              <h3 className="font-semibold text-sm">NET - {displayedTickers.map(t => formatStockDisplayName(t)).join(', ')} - {getMarketLabel(displayedMarket)}</h3>
            </div>
            <div className={`${showOnlyTotal ? 'flex justify-center' : 'w-full max-w-full'} flex-1 min-h-0`}>
              <div ref={netTableContainerRef} className={`${showOnlyTotal ? 'w-auto' : 'w-full max-w-full'} ${summaryByDate.size === 0 ? 'overflow-hidden' : 'overflow-x-auto overflow-y-auto'} border-l-2 border-r-2 border-b-2 border-white h-full`} style={{ scrollBehavior: 'auto' }}>
                <table ref={netTableRef} className={`${showOnlyTotal ? 'min-w-0' : summaryByDate.size === 0 ? 'w-full' : 'min-w-[1000px]'} ${getFontSizeClass()}`} style={{ tableLayout: summaryByDate.size === 0 ? 'fixed' : (isDataReady ? 'fixed' : 'auto'), width: summaryByDate.size === 0 ? '100%' : (showOnlyTotal ? 'auto' : undefined) }}>
                  <thead className="bg-[#3a4252] sticky top-0 z-10">
                    <tr className="border-t-2 border-white">
                      {!showOnlyTotal && datesForHeader.map((date, dateIndex) => (
                        <th key={date} className={`text-center py-[1px] px-[7.4px] font-bold text-white whitespace-nowrap ${dateIndex === 0 ? 'border-l-2 border-white' : ''} ${dateIndex < datesForHeader.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === datesForHeader.length - 1 ? 'border-r-[10px] border-white' : ''}`} colSpan={9} style={{ textAlign: 'center' }}>
                          {formatDisplayDate(date)}
                        </th>
                      ))}
                      <th className={`text-center py-[1px] px-[3.8px] font-bold text-white border-r-2 border-white ${showOnlyTotal || datesForHeader.length === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white'}`} colSpan={9} style={{ textAlign: 'center' }}>
                        Total
                      </th>
                    </tr>
                    <tr className="bg-[#3a4252]">
                      {!showOnlyTotal && datesForHeader.map((date, dateIndex) => (
                        <React.Fragment key={`detail-${date}`}>
                          {/* Net Buy Columns - No # */}
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white w-4 ${dateIndex === 0 ? 'border-l-2 border-white' : ''}`} style={{ textAlign: 'center' }}>BY</th>
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white w-6`} style={{ textAlign: 'center' }}>BLot</th>
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white w-6`} style={{ textAlign: 'center' }}>BVal</th>
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white w-6`} style={{ textAlign: 'center' }}>BAvg</th>
                          {/* Net Sell Columns - Keep # */}
                          <th className="text-center py-[1px] px-[5.4px] font-bold text-white bg-[#3a4252] w-4" style={{ textAlign: 'center' }}>#</th>
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white w-4`} style={{ textAlign: 'center' }}>SL</th>
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white w-6`} style={{ textAlign: 'center' }}>SLot</th>
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white w-6`} style={{ textAlign: 'center' }}>SVal</th>
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white w-6 ${dateIndex < datesForHeader.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === datesForHeader.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ textAlign: 'center' }}>SAvg</th>
                        </React.Fragment>
                      ))}
                      {/* Total Columns - Include BAvg and SAvg */}
                      <th className={`text-center py-[1px] px-[4.5px] font-bold text-white ${showOnlyTotal || datesForHeader.length === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white'}`} style={{ textAlign: 'center' }}>BY</th>
                      <th className={`text-center py-[1px] px-[4.5px] font-bold text-white`} style={{ textAlign: 'center' }}>BLot</th>
                      <th className={`text-center py-[1px] px-[4.5px] font-bold text-white`} style={{ textAlign: 'center' }}>BVal</th>
                      <th className={`text-center py-[1px] px-[4.5px] font-bold text-white`} style={{ textAlign: 'center' }}>BAvg</th>
                      <th className="text-center py-[1px] px-[5.4px] font-bold text-white bg-[#3a4252]" style={{ textAlign: 'center' }}>#</th>
                      <th className={`text-center py-[1px] px-[4.5px] font-bold text-white`} style={{ textAlign: 'center' }}>SL</th>
                      <th className={`text-center py-[1px] px-[4.5px] font-bold text-white`} style={{ textAlign: 'center' }}>SLot</th>
                      <th className={`text-center py-[1px] px-[4.5px] font-bold text-white`} style={{ textAlign: 'center' }}>SVal</th>
                      <th className={`text-center py-[1px] px-[6.3px] font-bold text-white border-r-2 border-white`} style={{ textAlign: 'center' }}>SAvg</th>
                    </tr>
                  </thead>
                  <tbody className="text-[12px]">
                    {(() => {
                      // Get broker text color class
                      const getBrokerColorClass = (brokerCode: string): string => {
                        if (GOVERNMENT_BROKERS.includes(brokerCode)) {
                          return 'text-green-600 font-semibold';
                        }
                        if (FOREIGN_BROKERS.includes(brokerCode)) {
                          return 'text-red-600 font-semibold';
                        }
                        return 'text-white font-semibold';
                      };

                      // Direct 1:1 Mapping - No Date-based Swapping
                      // Backend is consistent now.

                      // OPTIMIZED: Use memoized total data
                      const sortedTotalNetBuy = totalNetDataMemo.buy;
                      const sortedTotalNetSell = totalNetDataMemo.sell;


                      // Top 5 broker colors: Merah, Kuning, Hijau, Biru, Coklat
                      const bgColors = [
                        'hsl(0, 70%, 50%)',    // Merah (Red)
                        'hsl(50, 100%, 30%)',  // Kuning (Yellow) - further darkened for better contrast with white text
                        'hsl(120, 70%, 30%)',  // Hijau (Green) - further darkened for better contrast with white text
                        'hsl(210, 70%, 50%)',  // Biru (Blue)
                        'hsl(25, 80%, 40%)'    // Coklat (Brown)
                      ];

                      // IMPORTANT: Data is SWAPPED - mapping must be adjusted
                      // Kolom BY (BLot, BVal, BAvg) displays NetSell data → lock top 5 NetSell from Total
                      // Kolom SL (SLot, SVal, SAvg) displays NetBuy data → lock top 5 NetBuy from Total

                      // Map top 5 NetBuy brokers (from Total) to background colors (Block Style)
                      // User Request: Top Net Buy -> Block Color
                      const netBuyBrokerBgMap = new Map<string, string>();
                      sortedTotalNetBuy.slice(0, 5).forEach((item, idx) => {
                        netBuyBrokerBgMap.set(item.broker, bgColors[idx] || '');
                      });

                      // Map top 5 NetSell brokers (from Total) to underline colors (Underline Style)
                      // User Request: Top Net Sell -> Underline
                      const netSellBrokerUnderlineMap = new Map<string, string>();
                      sortedTotalNetSell.slice(0, 5).forEach((item, idx) => {
                        netSellBrokerUnderlineMap.set(item.broker, bgColors[idx] || '');
                      });

                      // Generic helper for BG style (used for BY columns)
                      const getBgStyle = (color?: string): React.CSSProperties | undefined => {
                        return color ? { backgroundColor: color, color: 'white' } : undefined;
                      };

                      // Generic helper for Underline style (used for SL columns)
                      const getUnderlineStyle = (color?: string): React.CSSProperties | undefined => {
                        return color ? {
                          borderBottom: `2px solid transparent`,
                          paddingBottom: '7px',
                          paddingTop: '1px',
                          lineHeight: '1.1',
                          backgroundImage: `linear-gradient(to bottom, transparent calc(100% - 5px), ${color} calc(100% - 5px), ${color} calc(100% - 2px), transparent calc(100% - 2px))`,
                          backgroundSize: '100% 7px',
                          backgroundPosition: 'bottom',
                          backgroundRepeat: 'no-repeat'
                        } : undefined;
                      };

                      // Find max row count across all dates
                      let maxRows = 0;
                      availableDates.forEach(date => {
                        const sortedData = sortedNetDataByDateMemo.get(date);
                        maxRows = Math.max(maxRows, sortedData?.buy.length || 0, sortedData?.sell.length || 0);
                      });

                      // Also include total broker counts in maxRows (for Total column)
                      const totalNetBuyCount = sortedTotalNetBuy.length;
                      const totalNetSellCount = sortedTotalNetSell.length;
                      maxRows = Math.max(maxRows, totalNetBuyCount, totalNetSellCount);

                      // Ensure minimum 21 rows for consistent display
                      maxRows = Math.max(maxRows, 21);

                      // If no data, show "No Data Available" message
                      if (summaryByDate.size === 0) {
                        const totalCols = datesForHeader.length * 9 + 9; // 9 cols per date + 9 for Total
                        return (
                          <tr className="border-b-2 border-white">
                            <td
                              colSpan={totalCols}
                              className="text-center py-[2.06px] text-muted-foreground font-bold"
                              style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
                            >
                              No Data Available
                            </td>
                          </tr>
                        );
                      }

                      // Render all rows (scrollable container handles overflow)
                      return Array.from({ length: maxRows }).map((_, rowIdx) => {
                        return (
                          <tr key={rowIdx} className={`hover:bg-accent/50 ${rowIdx === maxRows - 1 ? 'border-b-2 border-white' : ''}`}>
                            {!showOnlyTotal && availableDates.map((date, dateIndex) => {
                              // OPTIMIZED: Use memoized sorted data
                              const sortedRowData = sortedNetDataByDateMemo.get(date);
                              const netBuyData = sortedRowData?.buy[rowIdx];
                              const netSellData = sortedRowData?.sell[rowIdx];

                              // Direct Mapping (No Swap)
                              // BY columns show NetBuy data
                              const nbLot = netBuyData?.netBuyVol || 0;
                              const nbVal = netBuyData?.netBuyValue || 0;
                              const nbAvg = netBuyData?.netBuyerAvg || 0;
                              const byBroker = netBuyData;

                              // SL columns show NetSell data
                              const nsLot = netSellData?.netSellVol || 0;
                              const nsVal = netSellData?.netSellValue || 0;
                              const nsAvg = netSellData?.netSellerAvg || 0;
                              const slBroker = netSellData;

                              // STYLE LOGIC: Based on BROKER IDENTITY (from Total Maps)

                              // Check BY Broker Identity
                              const byBrokerBlockColor = byBroker ? netBuyBrokerBgMap.get(byBroker.broker) : undefined;
                              const byBrokerUnderlineColor = byBroker ? netSellBrokerUnderlineMap.get(byBroker.broker) : undefined;

                              // Determine BY Column Style
                              let byColumnStyle: React.CSSProperties | undefined = undefined;
                              let netBuyBgStyle: React.CSSProperties | undefined = undefined; // compatibility variable for JSX check

                              if (byBrokerBlockColor) {
                                byColumnStyle = getBgStyle(byBrokerBlockColor);
                                netBuyBgStyle = byColumnStyle; // Set for JSX text color check
                              } else if (byBrokerUnderlineColor) {
                                byColumnStyle = getUnderlineStyle(byBrokerUnderlineColor);
                              }

                              // Check SL Broker Identity
                              const slBrokerBlockColor = slBroker ? netBuyBrokerBgMap.get(slBroker.broker) : undefined;
                              const slBrokerUnderlineColor = slBroker ? netSellBrokerUnderlineMap.get(slBroker.broker) : undefined;

                              // Determine SL Column Style
                              let slColumnStyle: React.CSSProperties | undefined = undefined;

                              if (slBrokerBlockColor) {
                                slColumnStyle = getBgStyle(slBrokerBlockColor);
                              } else if (slBrokerUnderlineColor) {
                                slColumnStyle = getUnderlineStyle(slBrokerUnderlineColor);
                              }
                              // If no BG style, text depends on column type (Red for SL usually).
                              // But wait, user previous code had logic for text color based on BG. 
                              // Since we only use Underline for SL, text remains Red? 
                              // Or did previous code apply BG to SL in some cases?
                              // "const sellBgStyle = slBroker ? getNetSellBgStyle(slBroker.broker) : undefined;" -> This function checked netBuyBrokerBgMap (Top NetSell).
                              // So yes, SL column COULD have BG color if it contained NetSell data (which in Swap mode, it doesn't).
                              // But in Normal mode (!useSwap), SL contains NetSell data. So it SHOULD have BG color?

                              // Re-evaluating Design Intent:
                              // "Kolom BY... displays NetSell data -> lock top 5 NetSell from Total... BG Color"
                              // "Kolom SL... displays NetBuy data -> lock top 5 NetBuy from Total... Underline"

                              // This implies standard Bandarmology design:
                              // "Accumulation/NetBuy" leader -> Underline?
                              // "Distribution/NetSell" leader -> Block?

                              // So:
                              // Top NetSell Leader -> ALWAYS Block (BG Color)
                              // Top NetBuy Leader -> ALWAYS Underline

                              // Applying this consistent rule:
                              // BY Column:
                              //   - If !useSwap (NetBuy Data): Check if Top NetBuy (Underline)? 
                              //   - If useSwap (NetSell Data): Check if Top NetSell (Block)? YES.

                              // SL Column:
                              //   - If !useSwap (NetSell Data): Check if Top NetSell (Block)? 
                              //   - If useSwap (NetBuy Data): Check if Top NetBuy (Underline)? YES.

                              // SO:
                              // Block (BG) is for NetSell Leaders.
                              // Underline is for NetBuy Leaders.

                              // Let's refine the logic for !useSwap:
                              // BY (NetBuy Data): Highlight Top NetBuy -> Underline?
                              // SL (NetSell Data): Highlight Top NetSell -> Block?

                              // Wait, existing code structure had:
                              // Left (BY) -> getNetBuyBgStyle (Block)
                              // Right (SL) -> getNetSellUnderlineStyle (Underline)

                              // If I flip the design to always follow "Left=Block, Right=Underline", then:
                              // !useSwap (Normal):
                              // Left (NetBuy) -> Block (Highlight Top NetBuy)
                              // Right (NetSell) -> Underline (Highlight Top NetSell)

                              // Let's stick to "Left=Block, Right=Underline" as layout consistency seems preferred in tables.

                              return (
                                <React.Fragment key={`${date}-${rowIdx}`}>
                                  {/* Net Buy Columns (BY) - Data depends on date */}
                                  <td className={`text-center py-[1px] px-[5.4px] w-4 font-bold ${dateIndex === 0 ? 'border-l-2 border-white' : ''} ${byBroker ? getBrokerColorClass(byBroker.broker) : ''}`} style={byColumnStyle}>
                                    {byBroker?.broker || '-'}
                                  </td>
                                  <td className={`text-right py-[1px] px-[1.4px] w-6 font-bold ${netBuyBgStyle ? '' : 'text-green-600'}`} style={{ ...byColumnStyle, fontVariantNumeric: 'tabular-nums' }}>
                                    {byBroker ? formatLot(nbLot / 100) : '-'}
                                  </td>
                                  <td className={`text-right py-[1px] px-[5.4px] w-6 font-bold ${netBuyBgStyle ? '' : 'text-green-600'}`} style={{ ...byColumnStyle, fontVariantNumeric: 'tabular-nums' }}>
                                    {byBroker ? formatNumber(nbVal) : '-'}
                                  </td>
                                  <td className={`text-right py-[1px] px-[5.4px] w-6 font-bold ${netBuyBgStyle ? '' : 'text-green-600'}`} style={{ ...byColumnStyle, fontVariantNumeric: 'tabular-nums' }}>
                                    {byBroker ? formatAverage(nbAvg) : '-'}
                                  </td>
                                  {/* Net Sell Columns (SL) - Data depends on date - Keep # */}
                                  <td className={`text-center py-[1px] px-[5.4px] text-white bg-[#3a4252] font-bold w-4 ${slBroker ? getBrokerColorClass(slBroker.broker) : ''}`}>{slBroker ? rowIdx + 1 : '-'}</td>
                                  <td className={`py-[1px] px-[5.4px] w-4 font-bold ${slBroker ? getBrokerColorClass(slBroker.broker) : ''} ${slColumnStyle && !slColumnStyle.backgroundImage ? 'text-white' : ''}`} style={slColumnStyle}>
                                    {slBroker?.broker || '-'}
                                  </td>
                                  <td className={`text-right py-[1px] px-[1.4px] w-6 font-bold ${slColumnStyle && !slColumnStyle.backgroundImage ? 'text-white' : 'text-red-600'}`} style={{ ...slColumnStyle, fontVariantNumeric: 'tabular-nums' }}>
                                    {slBroker ? formatLot(nsLot / 100) : '-'}
                                  </td>
                                  <td className={`text-right py-[1px] px-[5.4px] w-6 font-bold ${slColumnStyle && !slColumnStyle.backgroundImage ? 'text-white' : 'text-red-600'}`} style={{ ...slColumnStyle, fontVariantNumeric: 'tabular-nums' }}>
                                    {slBroker ? formatNumber(nsVal) : '-'}
                                  </td>
                                  <td className={`text-right py-[1px] px-[5.4px] w-6 font-bold ${slColumnStyle && !slColumnStyle.backgroundImage ? 'text-white' : 'text-red-600'} ${dateIndex < availableDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === availableDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ ...slColumnStyle, fontVariantNumeric: 'tabular-nums' }}>
                                    {slBroker ? formatAverage(nsAvg) : '-'}
                                  </td>
                                </React.Fragment>
                              );
                            })}
                            {/* Total Columns - Include BAvg and SAvg - Data depends on date range */}
                            {(() => {
                              const totalNetBuy = sortedTotalNetBuy[rowIdx];
                              const totalNetSell = sortedTotalNetSell[rowIdx];



                              // Determine what data to show in Total BY columns
                              // Always show Total Net Buy in Left Column (BY)
                              // Matches Daily Behavior: Left = Net Buy
                              const totalByBroker = totalNetBuy;
                              const totalByLot = totalNetBuy?.nblot || 0;
                              const totalByVal = totalNetBuy?.nbval || 0;
                              const totalByAvgCalculated = totalNetBuy && Math.abs(totalNetBuy.nblot) > 0
                                ? totalNetBuy.nbval / Math.abs(totalNetBuy.nblot)
                                : 0;

                              // Determine what data to show in Total SL columns
                              // Always show Total Net Sell in Right Column (SL)
                              // Matches Daily Behavior: Right = Net Sell
                              const totalSlBroker = totalNetSell;
                              const totalSlLot = totalNetSell?.nslot || 0;
                              const totalSlVal = totalNetSell?.nsval || 0;
                              const totalSlAvgCalculated = totalNetSell && Math.abs(totalNetSell.nslot) > 0
                                ? totalNetSell.nsval / Math.abs(totalNetSell.nslot)
                                : 0;

                              // Check Total BY Broker Identity
                              const totalByBrokerBlockColor = totalByBroker ? netBuyBrokerBgMap.get(totalByBroker.broker) : undefined;
                              const totalByBrokerUnderlineColor = totalByBroker ? netSellBrokerUnderlineMap.get(totalByBroker.broker) : undefined;

                              // Determine Total BY Column Style
                              let totalNetBuyBgStyle: React.CSSProperties | undefined = undefined;
                              if (totalByBrokerBlockColor) {
                                totalNetBuyBgStyle = getBgStyle(totalByBrokerBlockColor);
                              } else if (totalByBrokerUnderlineColor) {
                                // Use Underline style if broker is Top Net Seller
                                totalNetBuyBgStyle = getUnderlineStyle(totalByBrokerUnderlineColor);
                              }

                              // Check Total SL Broker Identity
                              const totalSlBrokerBlockColor = totalSlBroker ? netBuyBrokerBgMap.get(totalSlBroker.broker) : undefined;
                              const totalSlBrokerUnderlineColor = totalSlBroker ? netSellBrokerUnderlineMap.get(totalSlBroker.broker) : undefined;

                              // Determine Total SL Column Style
                              let totalSellUnderlineStyle: React.CSSProperties | undefined = undefined;
                              if (totalSlBrokerBlockColor) {
                                totalSellUnderlineStyle = getBgStyle(totalSlBrokerBlockColor);
                              } else if (totalSlBrokerUnderlineColor) {
                                totalSellUnderlineStyle = getUnderlineStyle(totalSlBrokerUnderlineColor);
                              }

                              return (
                                <React.Fragment>
                                  {/* Total BY columns - Data depends on date range */}
                                  <td className={`text-center py-[1px] px-[4.5px] font-bold ${showOnlyTotal || datesForHeader.length === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white'} ${totalByBroker ? getBrokerColorClass(totalByBroker.broker) : ''}`} style={totalNetBuyBgStyle}>
                                    {totalByBroker?.broker || '-'}
                                  </td>
                                  <td className={`text-right py-[1px] px-[4.5px] font-bold ${totalNetBuyBgStyle ? '' : 'text-green-600'}`} style={{ ...totalNetBuyBgStyle, fontVariantNumeric: 'tabular-nums' }}>
                                    {totalByBroker ? formatLot(totalByLot / 100) : '-'}
                                  </td>
                                  <td className={`text-right py-[1px] px-[4.5px] font-bold ${totalNetBuyBgStyle ? '' : 'text-green-600'}`} style={{ ...totalNetBuyBgStyle, fontVariantNumeric: 'tabular-nums' }}>
                                    {totalByBroker ? formatNumber(totalByVal) : '-'}
                                  </td>
                                  <td className={`text-right py-[1px] px-[4.5px] font-bold ${totalNetBuyBgStyle ? '' : 'text-green-600'}`} style={{ ...totalNetBuyBgStyle, fontVariantNumeric: 'tabular-nums' }}>
                                    {totalByBroker && totalByAvgCalculated > 0 ? formatAverage(totalByAvgCalculated) : '-'}
                                  </td>
                                  {/* Total SL columns - Data depends on date range */}
                                  <td className={`text-center py-[1px] px-[5.4px] text-white bg-[#3a4252] font-bold ${totalSlBroker ? getBrokerColorClass(totalSlBroker.broker) : ''}`}>{totalSlBroker ? rowIdx + 1 : '-'}</td>
                                  <td className={`py-[1px] px-[4.5px] font-bold ${totalSlBroker ? getBrokerColorClass(totalSlBroker.broker) : ''}`} style={totalSellUnderlineStyle}>
                                    {totalSlBroker?.broker || '-'}
                                  </td>
                                  <td className="text-right py-[1px] px-[4.5px] text-red-600 font-bold" style={{ ...totalSellUnderlineStyle, fontVariantNumeric: 'tabular-nums' }}>
                                    {totalSlBroker ? formatLot(totalSlLot / 100) : '-'}
                                  </td>
                                  <td className="text-right py-[1px] px-[4.5px] text-red-600 font-bold" style={{ ...totalSellUnderlineStyle, fontVariantNumeric: 'tabular-nums' }}>
                                    {totalSlBroker ? formatNumber(totalSlVal) : '-'}
                                  </td>
                                  <td className="text-right py-[1px] px-[6.3px] text-red-600 font-bold border-r-2 border-white" style={{ ...totalSellUnderlineStyle, fontVariantNumeric: 'tabular-nums' }}>
                                    {totalSlBroker && totalSlAvgCalculated > 0 ? formatAverage(totalSlAvgCalculated) : '-'}
                                  </td>
                                </React.Fragment>
                              );
                            })()}
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                  {/* Summary Section - Locked at bottom */}
                  <tfoot className="sticky bottom-0 z-10">
                    {/* Header Row for Summary */}
                    <tr className="bg-[#3a4252] border-t-2 border-white">
                      {/* Per-date headers (only shown when not showOnlyTotal) */}
                      {!showOnlyTotal && datesForHeader.map((date, dateIndex) => (
                        <React.Fragment key={`summary-header-${date}`}>
                          {/* Summary Headers - 4 columns spanning full date width (2-2-3-2 distribution) */}
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white ${dateIndex === 0 ? 'border-l-2 border-white' : ''}`} colSpan={2}>TVal</th>

                          <th className="text-center py-[1px] px-[5.4px] font-bold text-white" colSpan={2}>{netLabel}</th>
                          <th className="text-center py-[1px] px-[5.4px] font-bold text-white" colSpan={3}>TLot</th>
                          <th className={`text-center py-[1px] px-[5.4px] font-bold text-white ${dateIndex < datesForHeader.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === datesForHeader.length - 1 ? 'border-r-[10px] border-white' : ''}`} colSpan={2}>Avg</th>
                        </React.Fragment>
                      ))}

                      {/* Total column headers */}
                      {/* Total column headers */}
                      <th className={`text-center py-[1px] px-[4.5px] font-bold text-white ${showOnlyTotal || datesForHeader.length === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white'}`} colSpan={2}>TVal</th>
                      <th className="text-center py-[1px] px-[4.5px] font-bold text-white" colSpan={2}>{netLabel}</th>
                      <th className="text-center py-[1px] px-[4.5px] font-bold text-white" colSpan={3}>TLot</th>
                      <th className="text-center py-[1px] px-[4.5px] font-bold text-white border-r-2 border-white" colSpan={2}>Avg</th>
                    </tr>

                    {/* Summary Data Row */}
                    <tr className="bg-[#0a0f20]">
                      {/* Per-date summary columns (only shown when not showOnlyTotal) */}
                      {!showOnlyTotal && availableDates.map((date, dateIndex) => {
                        // Calculate totals for this specific date using same logic as Total table
                        const dateData = summaryByDate.get(date) || [];

                        // MAIN TABLE TOTALS
                        let dateBuyTotalValue = 0;
                        let dateSellTotalValue = 0;
                        let dateBuyTotalLotShares = 0;
                        let dateSellTotalLotShares = 0;

                        dateData.forEach(item => {
                          if (!brokerFDScreen(item.broker)) return;

                          dateBuyTotalValue += Number(item.buyerValue) || 0;
                          dateSellTotalValue += Number(item.sellerValue) || 0;
                          dateBuyTotalLotShares += Number(item.buyerVol) || 0;
                          dateSellTotalLotShares += Number(item.sellerVol) || 0;
                        });

                        const totalValue = (dateBuyTotalValue + dateSellTotalValue) / 2;
                        const totalShares = (dateBuyTotalLotShares + dateSellTotalLotShares) / 2;
                        const totalLot = totalShares / 100;
                        const avgPrice = totalShares > 0 ? totalValue / totalShares : 0;

                        // Dynamic Net Value logic based on displayed investor type
                        let netValue = 0;
                        if (displayedFdFilter === 'Domestic') {
                          // DNVal: Sum NetBuy - NetSell from domestic rows (already filtered by main fetch)
                          dateData.forEach(item => {
                            if (!brokerFDScreen(item.broker)) return;
                            netValue += (Number(item.netBuyValue) || 0) - (Number(item.netSellValue) || 0);
                          });
                        } else if (displayedFdFilter === 'Foreign') {
                          // FNVal: Sum NetBuy - NetSell from foreign rows (already filtered by main fetch)
                          dateData.forEach(item => {
                            if (!brokerFDScreen(item.broker)) return;
                            netValue += (Number(item.netBuyValue) || 0) - (Number(item.netSellValue) || 0);
                          });
                        } else {
                          // F/D is 'All': Show FNVal. We try to use foreignSummaryByDate (using selected Board).
                          const foreignData = foreignSummaryByDate.get(date) || [];
                          if (foreignData.length > 0) {
                            foreignData.forEach(item => {
                              netValue += (Number(item.netBuyValue) || 0) - (Number(item.netSellValue) || 0);
                            });
                          } else {
                            // Fallback if foreignSummaryByDate is empty (e.g. during progressive load): Sum up foreign brokers
                            dateData.forEach(item => {
                              if (!brokerFDScreen(item.broker)) return;
                              const brokerCode = (item.broker || '').toUpperCase();
                              if (brokerCode && FOREIGN_BROKERS.includes(brokerCode)) {
                                netValue += (Number(item.buyerValue) - Number(item.sellerValue));
                              }
                            });
                          }
                        }

                        const netClass = netValue > 0 ? 'text-green-500' : netValue < 0 ? 'text-red-500' : 'text-white';

                        return (
                          <React.Fragment key={`summary-${date}`}>
                            {/* Summary Data - 4 columns centered */}
                            <td className={`text-center py-[2px] px-[5.4px] font-bold text-white ${dateIndex === 0 ? 'border-l-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={2}>{formatNumber(totalValue)}</td>

                            <td className={`text-center py-[2px] px-[5.4px] font-bold ${netClass}`} style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={2}>{formatNumber(netValue)}</td>
                            <td className="text-center py-[2px] px-[5.4px] font-bold text-white" style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={3}>{formatLot(totalLot)}</td>
                            <td className={`text-center py-[2px] px-[5.4px] font-bold text-white ${dateIndex < availableDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === availableDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={2}>{formatAverage(avgPrice)}</td>
                          </React.Fragment>
                        );
                      })}

                      {/* Total column summary - using grand totals */}
                      {(() => {
                        // Calculate grand totals across all dates (same as Total table)
                        let grandBuyTotalValue = 0;
                        let grandSellTotalValue = 0;
                        let grandBuyTotalLotShares = 0;
                        let grandSellTotalLotShares = 0;
                        let grandNetValue = 0;

                        availableDates.forEach(date => {
                          const dateData = summaryByDate.get(date) || [];
                          dateData.forEach(item => {
                            if (!brokerFDScreen(item.broker)) return;
                            grandBuyTotalValue += Number(item.buyerValue) || 0;
                            grandSellTotalValue += Number(item.sellerValue) || 0;
                            grandBuyTotalLotShares += Number(item.buyerVol) || 0;
                            grandSellTotalLotShares += Number(item.sellerVol) || 0;
                          });

                          // Aggregate separate Foreign/Domestic Data for Grand Total Net Value
                          if (displayedFdFilter === 'Domestic') {
                            dateData.forEach(item => {
                              if (!brokerFDScreen(item.broker)) return;
                              grandNetValue += (Number(item.netBuyValue) || 0) - (Number(item.netSellValue) || 0);
                            });
                          } else if (displayedFdFilter === 'Foreign') {
                            dateData.forEach(item => {
                              if (!brokerFDScreen(item.broker)) return;
                              grandNetValue += (Number(item.netBuyValue) || 0) - (Number(item.netSellValue) || 0);
                            });
                          } else {
                            const foreignData = foreignSummaryByDate.get(date) || [];
                            if (foreignData.length > 0) {
                              foreignData.forEach(item => {
                                grandNetValue += (Number(item.netBuyValue) || 0) - (Number(item.netSellValue) || 0);
                              });
                            } else {
                              // Fallback
                              dateData.forEach(item => {
                                if (!brokerFDScreen(item.broker)) return;
                                const brokerCode = (item.broker || '').toUpperCase();
                                if (brokerCode && FOREIGN_BROKERS.includes(brokerCode)) {
                                  grandNetValue += (Number(item.buyerValue) - Number(item.sellerValue));
                                }
                              });
                            }
                          }
                        });

                        const grandTotalValue = (grandBuyTotalValue + grandSellTotalValue) / 2;
                        const totalShares = (grandBuyTotalLotShares + grandSellTotalLotShares) / 2;
                        const grandTotalLot = totalShares / 100;
                        const grandAvgPrice = totalShares > 0 ? grandTotalValue / totalShares : 0;
                        const grandNetClass = grandNetValue > 0 ? 'text-green-500' : grandNetValue < 0 ? 'text-red-500' : 'text-white';

                        return (
                          <>
                            <td className={`text-center py-[2px] px-[4.5px] font-bold text-white ${showOnlyTotal || datesForHeader.length === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white'}`} style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={2}>{formatNumber(grandTotalValue)}</td>
                            <td className={`text-center py-[2px] px-[4.5px] font-bold ${grandNetClass}`} style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={2}>{formatNumber(grandNetValue)}</td>
                            <td className="text-center py-[2px] px-[4.5px] font-bold text-white" style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={3}>{formatLot(grandTotalLot)}</td>
                            <td className="text-center py-[2px] px-[4.5px] font-bold text-white border-r-2 border-white" style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={2}>{formatAverage(grandAvgPrice)}</td>
                          </>
                        );
                      })()}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>

      </div >
    );
  };

  const SECTOR_COLORS: Record<string, string> = {
    'Financials': '#FFFFFF',
    'Energy': '#4169E1',
    'Basic Materials': '#00BFFF',
    'Consumer Cyclicals': '#800080',
    'Consumer Non-Cyclicals': '#FFD700',
    'Healthcare': '#FF8C00',
    'Industrials': '#00FFFF',
    'Infrastructures': '#8B4513',
    'Properties & Real Estate': '#708090',
    'Technology': '#FF69B4',
    'Transportation & Logistic': '#C3B091',
  };
  const getSectorColor = (stock: string): string => {
    const name = stock.startsWith('[SECTOR] ') ? stock.replace('[SECTOR] ', '') : stock;
    if (name === 'IDX' || name === 'IDX Composite') return '#4169E1';
    return SECTOR_COLORS[name] || '#FFFFFF';
  };

  return (
    <div className="w-full flex flex-col h-full overflow-hidden bg-[#0a0f20] relative">
      {/* AI Button (Absolute Top Right) - stealth: sangat transparan, hover tetap gelap kayak screenshot */}
      <button
        onClick={() => setShowAIPopup(!showAIPopup)}
        className="absolute top-2 right-4 z-50 w-8 h-8 rounded-full bg-[#1a2332]/40 text-white/20 border border-white/[0.06] flex items-center justify-center hover:bg-[#1a2332]/50 hover:text-white/30 hover:border-white/10 opacity-[0.08] hover:opacity-30 shadow-none transition-all duration-200"
        title="AI Insights"
      >
        <span className="text-[11px] font-bold">AI</span>
      </button>

      {showAIPopup && (
        <div className="absolute top-11 right-4 z-[60] bg-popover border border-border rounded-lg shadow-xl overflow-hidden w-max">
          <div className="bg-muted px-3 py-1.5 border-b border-border flex justify-between items-center">
            <h4 className="font-semibold text-xs flex items-center gap-1.5">
              <Lightbulb className="w-3.5 h-3.5 text-indigo-500" />
              AI Says...
            </h4>
            <button onClick={() => setShowAIPopup(false)} className="text-muted-foreground hover:text-foreground">
              &times;
            </button>
          </div>
          <div className="p-0">
            <table className="w-full text-xs">
              <thead className="bg-[#3a4252] text-white">
                <tr>
                  <th className="py-1.5 px-2 text-left font-semibold border-r border-white/20 border-b border-white/20">Broker</th>
                  <th className="py-1.5 px-2 text-center font-semibold border-r border-white/20 border-b border-white/20">Acc</th>
                  <th className="py-1.5 px-2 text-center font-semibold border-r border-white/20 border-b border-white/20">Cons</th>
                  <th className="py-1.5 px-2 text-center font-semibold border-r border-white/20 border-b border-white/20">Dom</th>
                  <th className="py-1.5 px-2 text-center font-semibold border-r border-white/20 border-b border-white/20">BM</th>
                  <th className="py-1.5 px-2 text-center font-semibold border-r border-white/20 border-b border-white/20">SM</th>
                  <th className="py-1.5 px-2 text-center font-semibold border-b border-white/20" title="Floating PnL (Rp) = (Close - AvgBuy) × NetBuyVol — nominal Unrealized Profit/Loss, companion to Smart Money (%)">Floating</th>
                </tr>
              </thead>
              <tbody>
                {aiLevel1Stats.map((stat, i) => {
                  const accRaw = (stat as any).accumulationRaw ?? 0;
                  const accBg = accRaw > 75 ? '#16a34a' : accRaw > 50 ? '#ca8a04' : accRaw > 25 ? '#ea580c' : '#dc2626';
                  return (
                  <tr
                    key={stat.broker}
                    className={i % 2 === 0 ? 'bg-background' : 'bg-muted/30'}
                  >
                    <td className="py-1 px-2 border-r border-white/20 font-medium">
                      <span className="inline-flex items-center">
                        {stat.broker}
                        {stat.buyStreak >= 2 && stat.buyStreak < stat.totalDays ? (
                          <span
                            className="ml-0.5 text-amber-400 font-bold cursor-help leading-none"
                            title={`Late start accumulation: Buy last ${stat.buyStreak} days in a row`}
                          >
                            *
                          </span>
                        ) : stat.anyBuyStreak >= 2 && stat.anyBuyStreak < stat.totalDays && stat.buyStreak < 2 ? (
                          <span
                            className="ml-0.5 text-amber-300 font-bold cursor-help leading-none"
                            title={`Buy streak: last ${stat.anyBuyStreak} days in a row`}
                          >
                            *
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td className="py-1 px-2 border-r border-white/20 text-center font-medium text-white" style={{ backgroundColor: accBg }}>{stat.accumulation}</td>
                    <td className="py-1 px-2 border-r border-white/20 text-center">{stat.consistency}</td>
                    <td className="py-1 px-2 border-r border-white/20 text-center">{stat.domination}</td>
                    <td className="py-1 px-2 border-r border-white/20 text-center">{stat.bigMoney}</td>
                    <td className={`py-1 px-2 border-r border-white/20 text-center font-medium ${stat.rawSmartMoney > 0 ? 'text-green-600' : stat.rawSmartMoney < 0 ? 'text-red-600' : ''}`}>{stat.smartMoney}</td>
                    <td className={`py-1 px-2 text-center font-medium ${stat.rawFloating > 0 ? 'text-green-600' : stat.rawFloating < 0 ? 'text-red-600' : ''}`}>{stat.floating}</td>
                  </tr>
                  );
                })}
                {aiLevel1Stats.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-3 text-center text-muted-foreground italic">No data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Controls - Compact without Card */}
      <div className="bg-card border-b border-[#3a4252] px-4 py-1.5 z-40 pr-14" ref={menuContainerRef}>
        <div className="flex flex-col md:flex-row md:flex-wrap items-center gap-1 md:gap-x-7 md:gap-y-0.5">
          {/* Ticker Selection - Dropdown only (hidden in dashboard mode) */}
          {!isDashboard && (
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Ticker:</label>
            <div className="flex items-center gap-2 w-full md:w-auto">
              {/* Ticker Input */}
              <div className="relative flex-1 md:flex-none" ref={dropdownRef}>
                {disableTickerSelection ? (
                  <div className="w-full md:w-32 h-9 pl-3 pr-3 flex items-center text-sm border border-input rounded-md bg-muted text-foreground">
                    {propSelectedStock ? formatStockDisplayName(propSelectedStock) : (selectedTickers.length > 0 ? formatStockDisplayName(selectedTickers[0]!) : 'No ticker selected')}
                  </div>
                ) : (
                  <div className="w-full md:w-32 h-9 px-3 flex items-center justify-between bg-background border border-input rounded-md text-sm text-foreground hover:border-primary/50 focus-within:border-primary/50 transition-colors">
                    <Search className="w-3.5 h-3.5 text-muted-foreground mr-2 flex-shrink-0" />
                    <input
                      type="text"
                      className="w-full bg-transparent text-sm text-foreground focus:outline-none placeholder:text-muted-foreground/50 truncate"
                      placeholder="Select ticker"
                      value={showStockSuggestions ? tickerInput : (selectedTickers.length > 0 ? selectedTickers.map(t => formatStockDisplayName(t)).join(' | ') : '')}
                      onChange={(e) => {
                        handleStockInputChange(e.target.value);
                        setShowStockSuggestions(true);
                      }}
                      onFocus={() => setShowStockSuggestions(true)}
                    />
                    <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0 cursor-pointer" onClick={() => setShowStockSuggestions(!showStockSuggestions)} />
                  </div>
                )}
                {!disableTickerSelection && showStockSuggestions && (
                  <div className="absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-50 max-h-96 overflow-hidden flex flex-col w-[340px]">
                    {availableStocks.length === 0 ? (
                      <div className="px-3 py-4 text-sm text-muted-foreground flex flex-col items-center justify-center gap-2">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                        <span>Updating stock list...</span>
                      </div>
                    ) : (
                      <>
                        {/* Selected Items Header + Chips */}
                        {selectedTickers.length > 0 && (
                          <div className="border-b border-[#3a4252] bg-popover">
                            <div className="px-3 py-1.5 text-xs text-muted-foreground flex items-center justify-between">
                              <span>Selected ({selectedTickers.length})</span>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleClearAllTickers(); }}
                                className="text-xs text-destructive hover:text-destructive/80 font-medium"
                              >
                                Clear
                              </button>
                            </div>
                            <div className="flex flex-col">
                              {selectedTickers.map(ticker => (
                                <div key={ticker} className="px-3 py-[2.06px] text-sm font-medium flex justify-between items-center hover:bg-muted"
                                  style={{ color: ticker.startsWith('[SECTOR] ') ? getSectorColor(ticker) : undefined }}>
                                  <span className={ticker.startsWith('[SECTOR] ') ? '' : 'text-primary'}>{formatStockDisplayName(ticker)}</span>
                                  <button
                                    type="button"
                                    onMouseDown={(e) => { e.stopPropagation(); handleRemoveTicker(ticker); }}
                                    className="text-muted-foreground hover:text-destructive transition-colors ml-2 text-xs"
                                  >×</button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Search Input (Removed) */}
                        {/* Search Results Section */}
                        <div className="flex flex-row flex-1 overflow-hidden">
                          {/* Left column: Stocks */}
                          <div className="w-[140px] shrink-0 border-r border-[#3a4252] overflow-y-auto">
                            {tickerInput === '' ? (
                              <>
                                <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                  Stocks ({availableStocks.filter(s => !s.startsWith('[SECTOR] ')).length})
                                </div>
                                {availableStocks.filter(s => !s.startsWith('[SECTOR] ')).map(stock => {
                                  const isSelected = selectedTickers.includes(stock);
                                  return (
                                    <div
                                      key={stock}
                                      onClick={() => {
                                        if (isSelected) {
                                          setSelectedTickers(selectedTickers.filter(t => t !== stock));
                                        } else {
                                          handleStockSelect(stock);
                                        }
                                      }}
                                      className={`px-3 py-[2.06px] hover:bg-muted cursor-pointer text-sm flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : ''}`}
                                    >
                                      <span>{stock}</span>
                                      {isSelected && <span className="text-primary text-xs">✓</span>}
                                    </div>
                                  );
                                })}
                              </>
                            ) : filteredStocksList.length > 0 ? (
                              <>
                                <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                  Stocks ({filteredStocksList.length})
                                </div>
                                {filteredStocksList.map(stock => {
                                  const isSelected = selectedTickers.includes(stock);
                                  return (
                                    <div
                                      key={stock}
                                      onClick={() => {
                                        if (isSelected) {
                                          setSelectedTickers(selectedTickers.filter(t => t !== stock));
                                        } else {
                                          handleStockSelect(stock);
                                        }
                                      }}
                                      className={`px-3 py-[2.06px] hover:bg-muted cursor-pointer text-sm flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : ''}`}
                                    >
                                      <span>{stock}</span>
                                      {isSelected && <span className="text-primary text-xs">✓</span>}
                                    </div>
                                  );
                                })}
                              </>
                            ) : (
                              <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                Stocks (0)
                              </div>
                            )}
                          </div>
                          {/* Right column: Sectors */}
                          <div className="flex-1 overflow-y-auto">
                            {tickerInput === '' ? (
                              <>
                                <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                  Sectors ({availableStocks.filter(s => s.startsWith('[SECTOR] ')).length})
                                </div>
                                {availableStocks.filter(s => s.startsWith('[SECTOR] ')).map(stock => {
                                  const isSelected = selectedTickers.includes(stock);
                                  return (
                                    <div
                                      key={stock}
                                      onClick={() => {
                                        if (isSelected) {
                                          setSelectedTickers(selectedTickers.filter(t => t !== stock));
                                        } else {
                                          handleStockSelect(stock);
                                        }
                                      }}
                                      className={`px-3 py-[2.06px] hover:bg-muted cursor-pointer text-sm flex justify-between items-center ${isSelected ? 'bg-accent/50' : ''}`}
                                      style={{ color: getSectorColor(stock) }}
                                    >
                                      <span>{formatStockDisplayName(stock)}</span>
                                      {isSelected && <span className="text-primary text-xs">✓</span>}
                                    </div>
                                  );
                                })}
                              </>
                            ) : filteredSectorsList.length > 0 ? (
                              <>
                                <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                  Sectors ({filteredSectorsList.length})
                                </div>
                                {filteredSectorsList.map(stock => {
                                  const isSelected = selectedTickers.includes(stock);
                                  return (
                                    <div
                                      key={stock}
                                      onClick={() => {
                                        if (isSelected) {
                                          setSelectedTickers(selectedTickers.filter(t => t !== stock));
                                        } else {
                                          handleStockSelect(stock);
                                        }
                                      }}
                                      className={`px-3 py-[2.06px] hover:bg-muted cursor-pointer text-sm flex justify-between items-center ${isSelected ? 'bg-accent/50' : ''}`}
                                      style={{ color: getSectorColor(stock) }}
                                    >
                                      <span>{formatStockDisplayName(stock)}</span>
                                      {isSelected && <span className="text-primary text-xs">✓</span>}
                                    </div>
                                  );
                                })}
                              </>
                            ) : (
                              <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                Sectors (0)
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          )}

          {/* Date Range */}
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Date Range:</label>
            <div className="flex items-center gap-2 w-full md:w-auto">
              <div
                className="relative h-9 flex-1 md:w-36 rounded-md border border-input bg-background cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => triggerDatePicker(startDateRef)}
              >
                <input
                  ref={startDateRef}
                  type="date"
                  value={formatDateForInput(startDate)}
                  onChange={(e) => {
                    const selectedDateStr = e.target.value;
                    const selectedDate = new Date(selectedDateStr);

                    // Check if date is before minimum date
                    if (selectedDateStr < MIN_DATE) {
                      showToast({
                        type: 'warning',
                        title: 'Tanggal Tidak Valid',
                        message: `Tanggal paling awal yang bisa dipilih adalah 10 Februari 2020. Silakan pilih tanggal setelahnya.`,
                      });
                      return;
                    }

                    // Check if date is after maximum available date - MUST BE FIRST CHECK
                    if (maxAvailableDate && maxAvailableDate.trim() !== '') {
                      // Compare dates properly (YYYY-MM-DD format)
                      if (selectedDateStr > maxAvailableDate) {
                        showToast({
                          type: 'warning',
                          title: 'Tanggal Tidak Valid',
                          message: `Tanggal paling baru yang tersedia adalah ${new Date(maxAvailableDate + 'T00:00:00').toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}. Silakan pilih tanggal sebelum atau sama dengan tanggal tersebut.`,
                        });
                        // Reset to previous value - prevent any state changes
                        e.target.value = formatDateForInput(startDate);
                        e.preventDefault();
                        e.stopPropagation();
                        // Force update input value to prevent any changes
                        if (startDateRef.current) {
                          startDateRef.current.value = formatDateForInput(startDate);
                        }
                        return;
                      }
                    }

                    // Check if start date is after end date
                    if (endDate && selectedDateStr > endDate) {
                      showToast({
                        type: 'warning',
                        title: 'Tanggal Tidak Valid',
                        message: 'Tanggal mulai tidak boleh lebih dari tanggal akhir. Silakan pilih tanggal yang valid.',
                      });
                      e.target.value = startDate;
                      if (startDateRef.current) {
                        startDateRef.current.value = startDate;
                      }
                      return;
                    }

                    const dayOfWeek = selectedDate.getDay();
                    if (dayOfWeek === 0 || dayOfWeek === 6) {
                      showToast({
                        type: 'warning',
                        title: 'Hari Tidak Valid',
                        message: 'Tidak bisa memilih hari Sabtu atau Minggu. Silakan pilih hari kerja.',
                      });
                      // Reset input value to prevent any state changes
                      e.target.value = formatDateForInput(startDate);
                      e.preventDefault();
                      e.stopPropagation();
                      if (startDateRef.current) {
                        startDateRef.current.value = formatDateForInput(startDate);
                      }
                      // IMPORTANT: Early return - don't update any state, so reminder toast won't trigger
                      return;
                    }

                    // Calculate trading days in the new range
                    const start = new Date(e.target.value);
                    const end = new Date(endDate || e.target.value);
                    const tradingDays: string[] = [];
                    const current = new Date(start);

                    while (current <= end) {
                      const dayOfWeek = current.getDay();
                      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                        const dateString = current.toISOString().split('T')[0];
                        if (dateString && dateString >= MIN_DATE) {
                          // Check if date is within maximum available date
                          if (!maxAvailableDate || maxAvailableDate.trim() === '' || dateString <= maxAvailableDate) {
                            tradingDays.push(dateString);
                          } else {
                            // If any date in range is after maxAvailableDate, show toast and prevent change
                            showToast({
                              type: 'warning',
                              title: 'Tanggal Tidak Valid',
                              message: `Tanggal paling baru yang tersedia adalah ${new Date(maxAvailableDate).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}. Range tanggal yang dipilih mengandung tanggal setelah batas maksimum.`,
                            });
                            // Reset to previous value - prevent any state changes
                            e.target.value = startDate;
                            e.preventDefault();
                            e.stopPropagation();
                            // Force update input value to prevent any changes
                            if (startDateRef.current) {
                              startDateRef.current.value = startDate;
                            }
                            return;
                          }
                        } else if (dateString && dateString < MIN_DATE) {
                          // If any date in range is before MIN_DATE, show toast and prevent change
                          showToast({
                            type: 'warning',
                            title: 'Tanggal Tidak Valid',
                            message: `Tanggal paling awal yang bisa dipilih adalah 10 Februari 2020. Range tanggal yang dipilih mengandung tanggal sebelum batas minimum.`,
                          });
                          // Reset to previous value - prevent any state changes
                          e.target.value = startDate;
                          e.preventDefault();
                          e.stopPropagation();
                          // Force update input value to prevent any changes
                          if (startDateRef.current) {
                            startDateRef.current.value = startDate;
                          }
                          return;
                        }
                      }
                      current.setDate(current.getDate() + 1);
                    }

                    // Only update state if all validations passed
                    // CRITICAL: Reset shouldFetchData silently BEFORE updating dates to prevent auto-load
                    // This ensures data only changes when Show button is clicked
                    shouldFetchDataRef.current = false; // CRITICAL: Update ref first (synchronous) - SILENT
                    setShouldFetchData(false); // SILENT
                    setStartDate(e.target.value);
                    // Auto update end date if not set or if start > end
                    if (!endDate || new Date(e.target.value) > new Date(endDate)) {
                      setEndDate(e.target.value);
                    }
                    setSelectedDates(tradingDays);
                  }}
                  onKeyDown={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  onInput={(e) => e.preventDefault()}
                  min={MIN_DATE}
                  max={maxAvailableDate && maxAvailableDate.trim() !== '' ? formatDateForInput(maxAvailableDate) : (endDate ? formatDateForInput(endDate) : new Date().toISOString().split('T')[0] || undefined)}
                  disabled={false}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  style={{ caretColor: 'transparent' }}
                />
                <div className="flex items-center justify-between h-full px-3">
                  <span className="text-sm text-foreground">
                    {startDate ? new Date(startDate).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    }) : 'DD/MM/YYYY'}
                  </span>
                  <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </div>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap hidden md:inline">to</span>
              <div
                className="relative h-9 flex-1 md:w-36 rounded-md border border-input bg-background cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => triggerDatePicker(endDateRef)}
              >
                <input
                  ref={endDateRef}
                  type="date"
                  value={formatDateForInput(endDate)}
                  onChange={(e) => {
                    const selectedDateStr = e.target.value;
                    const selectedDate = new Date(selectedDateStr);

                    // Check if date is before minimum date
                    if (selectedDateStr < MIN_DATE) {
                      showToast({
                        type: 'warning',
                        title: 'Tanggal Tidak Valid',
                        message: `Tanggal paling awal yang bisa dipilih adalah 10 Februari 2020. Silakan pilih tanggal setelahnya.`,
                      });
                      return;
                    }

                    // Check if date is after maximum available date - MUST BE FIRST CHECK
                    if (maxAvailableDate && maxAvailableDate.trim() !== '') {
                      // Compare dates properly (YYYY-MM-DD format)
                      if (selectedDateStr > maxAvailableDate) {
                        showToast({
                          type: 'warning',
                          title: 'Tanggal Tidak Valid',
                          message: `Tanggal paling baru yang tersedia adalah ${new Date(maxAvailableDate + 'T00:00:00').toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}. Silakan pilih tanggal sebelum atau sama dengan tanggal tersebut.`,
                        });
                        // Reset to previous value - prevent any state changes
                        e.target.value = formatDateForInput(endDate);
                        e.preventDefault();
                        e.stopPropagation();
                        // Force update input value to prevent any changes
                        if (endDateRef.current) {
                          endDateRef.current.value = formatDateForInput(endDate);
                        }
                        return;
                      }
                    }

                    // Check if end date is before start date
                    if (startDate && selectedDateStr < startDate) {
                      showToast({
                        type: 'warning',
                        title: 'Tanggal Tidak Valid',
                        message: 'Tanggal akhir tidak boleh kurang dari tanggal mulai. Silakan pilih tanggal yang valid.',
                      });
                      e.target.value = endDate;
                      if (endDateRef.current) {
                        endDateRef.current.value = endDate;
                      }
                      return;
                    }

                    const dayOfWeek = selectedDate.getDay();
                    if (dayOfWeek === 0 || dayOfWeek === 6) {
                      showToast({
                        type: 'warning',
                        title: 'Hari Tidak Valid',
                        message: 'Tidak bisa memilih hari Sabtu atau Minggu. Silakan pilih hari kerja.',
                      });
                      // Reset input value to prevent any state changes
                      e.target.value = formatDateForInput(endDate);
                      e.preventDefault();
                      e.stopPropagation();
                      if (endDateRef.current) {
                        endDateRef.current.value = formatDateForInput(endDate);
                      }
                      // IMPORTANT: Early return - don't update any state, so reminder toast won't trigger
                      return;
                    }

                    // Calculate trading days in the new range
                    const start = new Date(startDate);
                    const end = new Date(e.target.value);
                    const tradingDays: string[] = [];
                    const current = new Date(start);

                    while (current <= end) {
                      const dayOfWeek = current.getDay();
                      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                        const dateString = current.toISOString().split('T')[0];
                        if (dateString && dateString >= MIN_DATE) {
                          // Check if date is within maximum available date
                          if (!maxAvailableDate || maxAvailableDate.trim() === '' || dateString <= maxAvailableDate) {
                            tradingDays.push(dateString);
                          } else {
                            // If any date in range is after maxAvailableDate, show toast and prevent change
                            showToast({
                              type: 'warning',
                              title: 'Tanggal Tidak Valid',
                              message: `Tanggal paling baru yang tersedia adalah ${new Date(maxAvailableDate).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}. Range tanggal yang dipilih mengandung tanggal setelah batas maksimum.`,
                            });
                            // Reset to previous value - prevent any state changes
                            e.target.value = formatDateForInput(endDate);
                            e.preventDefault();
                            e.stopPropagation();
                            // Force update input value to prevent any changes
                            if (endDateRef.current) {
                              endDateRef.current.value = formatDateForInput(endDate);
                            }
                            return;
                          }
                        } else if (dateString && dateString < MIN_DATE) {
                          // If any date in range is before MIN_DATE, show toast and prevent change
                          showToast({
                            type: 'warning',
                            title: 'Tanggal Tidak Valid',
                            message: `Tanggal paling awal yang bisa dipilih adalah 10 Februari 2020. Range tanggal yang dipilih mengandung tanggal sebelum batas minimum.`,
                          });
                          // Reset to previous value - prevent any state changes
                          e.target.value = formatDateForInput(endDate);
                          e.preventDefault();
                          e.stopPropagation();
                          // Force update input value to prevent any changes
                          if (endDateRef.current) {
                            endDateRef.current.value = formatDateForInput(endDate);
                          }
                          return;
                        }
                      }
                      current.setDate(current.getDate() + 1);
                    }

                    // Only update state if all validations passed
                    // CRITICAL: Reset shouldFetchData silently BEFORE updating dates to prevent auto-load
                    // This ensures data only changes when Show button is clicked
                    shouldFetchDataRef.current = false; // CRITICAL: Update ref first (synchronous) - SILENT
                    setShouldFetchData(false); // SILENT
                    // Update dates - NO effect will run, completely silent
                    setEndDate(e.target.value);
                    setSelectedDates(tradingDays);
                  }}
                  onKeyDown={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  onInput={(e) => e.preventDefault()}
                  min={startDate ? formatDateForInput(startDate) : MIN_DATE}
                  max={maxAvailableDate && maxAvailableDate.trim() !== '' ? formatDateForInput(maxAvailableDate) : new Date().toISOString().split('T')[0] || undefined}
                  disabled={false}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  style={{ caretColor: 'transparent' }}
                />
                <div className="flex items-center justify-between h-full px-3">
                  <span className="text-sm text-foreground">
                    {endDate ? new Date(endDate).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    }) : 'DD/MM/YYYY'}
                  </span>
                  <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </div>
              </div>
            </div>
          </div>

          {/* F/D Filter */}
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">F/D:</label>
            <select
              value={fdFilter}
              onChange={(e) => {
                setFdFilter(e.target.value as 'All' | 'Foreign' | 'Domestic');
                // CRITICAL: Keep existing data visible - no auto-fetch, no hide tables
                // User must click Show button to fetch new data
              }}
              className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto"
            >
              <option value="All">All</option>
              <option value="Foreign">Foreign</option>
              <option value="Domestic">Domestic</option>
            </select>
          </div>

          {/* Market Filter */}
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Board:</label>
            <select
              value={marketFilter}
              onChange={(e) => {
                setMarketFilter(e.target.value as 'RG' | 'TN' | 'NG' | '');
                // CRITICAL: Keep existing data visible - no auto-fetch, no hide tables
                // User must click Show button to fetch new data
              }}
              className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto"
            >
              <option value="">All</option>
              <option value="RG">RG</option>
              <option value="TN">TN</option>
              <option value="NG">NG</option>
            </select>
          </div>

          {/* Show Button */}
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <button
              onClick={() => {
                if (selectedTickers.length === 0 || selectedDates.length === 0) {
                  showToast({
                    type: 'warning',
                    title: 'Data Tidak Lengkap',
                    message: 'Silakan pilih ticker dan tanggal terlebih dahulu.',
                  });
                  return;
                }

                // Validate that all selected dates are within maxAvailableDate
                if (maxAvailableDate && maxAvailableDate.trim() !== '') {
                  const invalidDates = selectedDates.filter(date => date > maxAvailableDate);
                  if (invalidDates.length > 0) {
                    showToast({
                      type: 'warning',
                      title: 'Tanggal Tidak Valid',
                      message: `Tanggal paling baru yang tersedia adalah ${new Date(maxAvailableDate).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}. Beberapa tanggal yang dipilih (${invalidDates.join(', ')}) melebihi batas maksimum.`,
                    });
                    return;
                  }
                }

                // Update displayed values when Show button is clicked
                setDisplayedFdFilter(fdFilter);
                setDisplayedTickers([...selectedTickers]);
                setDisplayedMarket(marketFilter);

                // Check if only investor filter changed (ticker, dates, and market are the same)
                const currentParams = {
                  tickers: [...selectedTickers].sort().join(','),
                  dates: [...selectedDates].sort().join(','),
                  market: marketFilter || '',
                  fdFilter: fdFilter
                };
                const lastParams = lastFetchParams ? {
                  tickers: [...lastFetchParams.tickers].sort().join(','),
                  dates: [...lastFetchParams.dates].sort().join(','),
                  market: lastFetchParams.market || '',
                  fdFilter: lastFetchParams.fdFilter
                } : null;

                const onlyInvestorChanged = lastParams !== null &&
                  currentParams.tickers === lastParams.tickers &&
                  currentParams.dates === lastParams.dates &&
                  currentParams.market === lastParams.market &&
                  currentParams.fdFilter === lastParams.fdFilter &&
                  rawSummaryByDate.size > 0;

                if (onlyInvestorChanged) {
                  // Only investor filter changed - filter existing data without calling API
                  // Data is already in rawSummaryByDate, just apply investor filter
                  // summaryByDate will be updated by the filter effect below
                  // Note: displayedTickers and displayedMarket don't need to update (only investor changed)
                  setIsLoading(false);
                  setIsDataReady(true);
                  // No need to fetch API
                } else {
                  // Ticker, dates, or market changed - need to fetch new data from API
                  // Update last fetch params
                  setLastFetchParams({
                    tickers: [...selectedTickers],
                    dates: [...selectedDates],
                    market: marketFilter || '',
                    fdFilter: fdFilter
                  });

                  // Clear existing data and cache before fetching new data
                  setSummaryByDate(new Map());
                  setRawSummaryByDate(new Map());
                  setIsDataReady(false);
                  // Clear cache to force fresh fetch with current filters
                  dataCacheRef.current.clear();
                  // Trigger fetch by setting shouldFetchData (ref and state)
                  // CRITICAL: Set ref first (synchronous), then state (async)
                  shouldFetchDataRef.current = true;
                  setShouldFetchData(true);
                }
              }}
              disabled={isLoading || selectedTickers.length === 0 || selectedDates.length === 0}
              className="h-[34px] px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium whitespace-nowrap flex items-center justify-center w-full md:w-auto"
            >
              Show
            </button>
          </div>
        </div>
      </div>



      {/* Loading State - only show when actually loading (isLoading === true) */}
      {isLoading && (
        <div className="flex items-center justify-center py-16 pt-4">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Loading broker summary...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="flex items-center justify-center py-8 pt-4">
          <div className="text-sm text-destructive">{error}</div>
        </div>
      )}

      {/* Main Data Display - only show when not loading, no error, and data is ready */}
      <div className="flex-1 overflow-hidden pt-2">
        {!isLoading && !error && isDataReady && renderHorizontalView()}
      </div>
    </div>
  );
}
